import React, { useState, useEffect, useRef } from 'react';
import api from './services/api';
import './responsive.css';
import { useBreakpoint } from './hooks/useMediaQuery';
import * as XLSX from 'xlsx';

// ============= MOCK DATA BASED ON EXCEL =============
const initialMembers = [
  { id: 1, itsId: '30374481', name: 'Yusuf Bhai Alihusain Bhai Jawadwala', maleAdults: 1, femaleAdults: 1, children: 2, totalCount: 4, takhmeen: 22600, received: 15000, balance: 7600 },
  { id: 2, itsId: '12345678', name: 'Badshah', maleAdults: 1, femaleAdults: 0, children: 0, totalCount: 1, takhmeen: 6000, received: 6000, balance: 0 },
  { id: 3, itsId: '78611052', name: 'Saifuddin Bhai Batliwala', maleAdults: 0, femaleAdults: 0, children: 0, totalCount: 0, takhmeen: 53000, received: 53000, balance: 0 },
];

const expenseCategories = [
  'Nazrul Maqam', 'Stationery Expenses', 'Electrical, relay and Projector Expenses',
  'Transportation Expenses', 'House Keeping Expenses', 'Medical Expenses',
  'Miscellaneous Expenses', 'Payment to Caterer', 'Packaging Material',
  'Repair & Maintenance Expenses', 'Tips', 'Other Food & drink Expenses',
  'Janab Ikram', 'Janab Salaam'
];

const incomeCategories = ['Voluntarily Contribution Received', 'Takhmeen Collection', 'Donation', 'Other Income'];

// ============= UTILITY FUNCTIONS =============
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
  if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
  if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
  return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
};

const formatDate = (date) => new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

// Note: Local storage functions removed - now using API for data persistence



// Default data
const defaultReceipts = [
  { id: 1, receiptNo: 'R1447/01', date: '2026-01-27', itsId: '30374481', name: 'Yusuf Bhai Alihusain Bhai Jawadwala', amount: 15000, mode: 'Cash' },
  { id: 2, receiptNo: 'R1447/02', date: '2026-01-27', itsId: '12345678', name: 'Badshah', amount: 6000, mode: 'Cash' },
  { id: 3, receiptNo: 'R1447/03', date: '2026-01-27', itsId: '78611052', name: 'Saifuddin Bhai Batliwala', amount: 53000, mode: 'Cash' },
];

const defaultAccounts = { cash: 74000, bank: 0, openingBalance: 0 };

// ============= MAIN APP =============
export default function HatimiMarkazAccounts() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeModule, setActiveModule] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Responsive breakpoints
  const { isMobile, isTablet, isDesktop } = useBreakpoint();

  // Initialize with empty data - will load from API after login
  const [members, setMembers] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState({ openingBalance: 0, cash: 0, bank: 0 });

  // Note: Removed localStorage wrapper functions - now using API directly

  // Add print styles for half-page receipt printing
  useEffect(() => {
    const styleId = 'receipt-print-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @media print {
          @page {
            size: A4 portrait;
            margin: 15mm;
          }

          body {
            margin: 0;
            padding: 0;
          }

          body * {
            visibility: hidden;
          }

          .receipt-print-area,
          .receipt-print-area * {
            visibility: visible !important;
          }

          .receipt-print-area {
            position: absolute !important;
            left: 50% !important;
            top: 10mm !important;
            transform: translateX(-50%) !important;
          }

          .no-print {
            display: none !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);


  // ============= API DATA LOADING =============
  const loadDataFromAPI = async () => {
    try {
      // Load all data from API instead of local storage
      const [membersData, receiptsData, expensesData, dashboardData] = await Promise.all([
        api.getMembers(),
        api.getReceipts(),
        api.getExpenses(),
        api.getDashboard(),
      ]);

      // Transform API data to match frontend format
      setMembers(membersData.map(member => ({
        id: member.id,
        itsId: member.its_id,
        name: member.name,
        maleAdults: member.male_adults,
        femaleAdults: member.female_adults,
        children: member.children,
        totalCount: member.total_count,
        takhmeen: parseFloat(member.takhmeen_amount),
        received: parseFloat(member.total_received || 0),
        balance: parseFloat(member.balance || member.takhmeen_amount),
        fullNiyaz: parseFloat(member.full_niyaz || 0),
        halfNiyaz: parseFloat(member.half_niyaz || 0),
        zabihat: parseFloat(member.zabihat || 0),
        salawaat: parseFloat(member.salawaat || 0),
        niyazRaat: member.niyaz_raat || '',
        chairNumber: member.chair_number || '',
        izanCardNumber: member.izan_card_number || '',
        pirsaCount: parseInt(member.pirsa_count || 0),
        phone: member.phone || '',
        email: member.email || '',
        address: member.address || '',
        isLocked: member.is_locked,
        createdAt: member.created_at,
        updatedAt: member.updated_at
      })));

      setReceipts(receiptsData.map(receipt => ({
        id: receipt.id,
        receiptNo: receipt.receipt_no,
        date: receipt.receipt_date,
        itsId: receipt.its_id,
        name: receipt.name,
        amount: parseFloat(receipt.amount),
        mode: receipt.payment_mode,
        referenceId: receipt.reference_number || '',
        remarks: receipt.payment_remarks || '',
        createdBy: receipt.created_by,
        createdAt: receipt.created_at
      })));

      setExpenses(expensesData.map(expense => ({
        id: expense.id,
        voucherNo: expense.voucher_no,
        date: expense.expense_date,
        category: expense.category_name,
        amount: parseFloat(expense.amount),
        description: expense.description,
        paidTo: expense.paid_to,
        paymentFrom: expense.payment_from,
        createdBy: expense.created_by
      })));

      // Set accounts data with opening balance (starting with 0)
      setAccounts({
        openingBalance: 0,
        cash: parseFloat(dashboardData.cash_balance) || 0,
        bank: 0,
        expenseBifurcation: dashboardData.expense_bifurcation || []
      });

    } catch (error) {
      console.error('Failed to load data from API:', error);
    }
  };

  // Load data on app startup if user is already logged in
  useEffect(() => {
    if (api.isAuthenticated()) {
      const savedUser = localStorage.getItem('hatimi_user');
      if (savedUser) {
        setCurrentUser(JSON.parse(savedUser));
        setIsLoggedIn(true);
        loadDataFromAPI();
      }
    }
  }, []);

  // ============= LOGIN COMPONENT =============
  const LoginScreen = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      setError('');

      try {
        // First try API login
        const response = await api.login(username, password);
        setCurrentUser(response.user);
        setIsLoggedIn(true);
        console.log('✅ API Login successful:', response.user);
        // Load data from API after successful login
        await loadDataFromAPI();
      } catch (error) {
        console.log('❌ API Login failed:', error.message);
        setError(error.message || 'Login failed. Please check your credentials.');
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a1628 0%, #1a365d 50%, #0d2137 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Crimson Pro', Georgia, serif",
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative Islamic Pattern Background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c9a227' fill-opacity='0.05'%3E%3Cpath d='M30 0L60 30L30 60L0 30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.5
        }} />
        
        {/* Glowing orbs */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(201, 162, 39, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />

        <div
          className="login-form-container"
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(201, 162, 39, 0.2)',
            padding: '48px',
            width: '420px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            position: 'relative',
            zIndex: 10
          }}
        >
          {/* Logo/Header */}
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 50%, #c9a227 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(201, 162, 39, 0.3)',
              fontSize: '32px',
              color: '#0a1628',
              fontWeight: '700'
            }}>
              ح
            </div>
            <h1 style={{
              color: '#f4d03f',
              fontSize: '28px',
              fontWeight: '600',
              margin: '0 0 8px 0',
              letterSpacing: '1px'
            }}>HATIMI HILLS</h1>
            <p style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px',
              margin: 0,
              letterSpacing: '2px',
              textTransform: 'uppercase'
            }}>Kalimi Mohalla, Pune</p>
            <div style={{
              marginTop: '16px',
              padding: '8px 20px',
              background: 'rgba(201, 162, 39, 0.1)',
              borderRadius: '20px',
              display: 'inline-block',
              border: '1px solid rgba(201, 162, 39, 0.3)'
            }}>
              <span style={{ color: '#c9a227', fontSize: '13px', fontWeight: '500' }}>
                شهر الله المبارك ١٤٤٧
              </span>
            </div>
          </div>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '8px'
              }}>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#c9a227';
                  e.target.style.boxShadow = '0 0 0 3px rgba(201, 162, 39, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '8px'
              }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '15px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#c9a227';
                  e.target.style.boxShadow = '0 0 0 3px rgba(201, 162, 39, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                color: '#fca5a5',
                fontSize: '13px',
                textAlign: 'center'
              }}>{error}</div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#0a1628',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(201, 162, 39, 0.3)',
                opacity: isLoading ? 0.7 : 1
              }}
              onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              {isLoading ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  };

  // ============= SIDEBAR COMPONENT =============
  const Sidebar = () => {
    // Menu items for regular users (non-admin)
    const userMenuItems = [
      { id: 'dashboard', icon: '📊', label: 'Dashboard' },
      { id: 'receipts', icon: '🧾', label: 'Receipts' },
      { id: 'takhmeen', icon: '👥', label: 'Takhmeen' },
    ];

    // Inventory menu items (for inventory_manager and admin)
    const inventoryMenuItems = [
      { id: 'inventory-dashboard', icon: '📦', label: 'Inventory Dashboard' },
      { id: 'inventory-items', icon: '🏪', label: 'Inventory Items' },
      { id: 'daily-yadi', icon: '📋', label: 'Daily Yadi' },
      { id: 'purchase-orders', icon: '🛒', label: 'Purchase Orders' },
      { id: 'inventory-reports', icon: '📊', label: 'Inventory Reports' },
    ];

    // Additional menu items for admin only
    const adminMenuItems = [
      { id: 'cashbook', icon: '📒', label: 'Cash Book' },
      { id: 'expenses', icon: '💸', label: 'Expenses' },
      { id: 'reports', icon: '📈', label: 'Reports' },
      { id: 'categories', icon: '🏷️', label: 'Categories' },
      { id: 'users', icon: '👤', label: 'Users' },
    ];

    // Build menu based on role
    let menuItems = [...userMenuItems];

    // Add inventory items for inventory_manager and admin
    if (currentUser.role === 'inventory_manager' || currentUser.role === 'admin') {
      menuItems = [...menuItems, ...inventoryMenuItems];
    }

    // Add admin-only items
    if (currentUser.role === 'admin') {
      menuItems = [...menuItems, ...adminMenuItems];
    }

    return (
      <div
        className={`app-sidebar ${isSidebarOpen ? 'open' : ''}`}
        style={{
          width: '260px',
          background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
          borderRight: '1px solid rgba(201, 162, 39, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh'
        }}
      >
        {/* Brand */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              color: '#0a1628',
              fontWeight: '700'
            }}>ح</div>
            <div>
              <h2 style={{ margin: 0, color: '#f4d03f', fontSize: '16px', fontWeight: '600' }}>Hatimi Hills</h2>
              <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px' }}>Shehrullah 1447</p>
            </div>
          </div>
        </div>

        {/* Menu */}
        <nav style={{ padding: '16px 12px', flex: 1 }}>
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveModule(item.id);
                if (isMobile) setIsSidebarOpen(false);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: activeModule === item.id 
                  ? 'linear-gradient(90deg, rgba(201, 162, 39, 0.15) 0%, transparent 100%)' 
                  : 'transparent',
                border: 'none',
                borderLeft: activeModule === item.id ? '3px solid #c9a227' : '3px solid transparent',
                borderRadius: '0 8px 8px 0',
                color: activeModule === item.id ? '#f4d03f' : 'rgba(255, 255, 255, 0.6)',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '4px',
                textAlign: 'left'
              }}
              onMouseOver={(e) => {
                if (activeModule !== item.id) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.03)';
                  e.target.style.color = '#fff';
                }
              }}
              onMouseOut={(e) => {
                if (activeModule !== item.id) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgba(255, 255, 255, 0.6)';
                }
              }}
            >
              <span style={{ fontSize: '18px' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* User Section */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: '600',
              fontSize: '14px'
            }}>{currentUser?.name?.charAt(0)}</div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, color: '#fff', fontSize: '13px', fontWeight: '500' }}>{currentUser?.name}</p>
              <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px', textTransform: 'capitalize' }}>{currentUser?.role}</p>
            </div>
            <button
              onClick={() => setIsLoggedIn(false)}
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '8px',
                cursor: 'pointer',
                color: '#fca5a5',
                fontSize: '14px'
              }}
              title="Logout"
            >↪</button>
          </div>
          <p style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '10px', margin: '8px 0 0 0', textAlign: 'center' }}>
            Data saved in browser storage
          </p>
        </div>
      </div>
    );
  };

  // ============= DASHBOARD COMPONENT =============
  const Dashboard = () => {
    const totalIncome = receipts.reduce((sum, r) => sum + r.amount, 0);
    const cashReceipts = receipts.filter(r => r.mode === 'Cash').reduce((sum, r) => sum + r.amount, 0);
    const onlineReceipts = receipts.filter(r => ['UPI', 'Bank Transfer', 'Cheque'].includes(r.mode)).reduce((sum, r) => sum + r.amount, 0);
    const cashReceiptsCount = receipts.filter(r => r.mode === 'Cash').length;
    const onlineReceiptsCount = receipts.filter(r => ['UPI', 'Bank Transfer', 'Cheque'].includes(r.mode)).length;
    const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
    const kalimiBifurcation = (accounts.expenseBifurcation || []).find(b => b.source === 'Kalimi');
    const hatimiBifurcation = (accounts.expenseBifurcation || []).find(b => b.source === 'Hatmi Markaz');
    const kalimiExpenses = kalimiBifurcation ? parseFloat(kalimiBifurcation.total) : 0;
    const hatimiExpenses = hatimiBifurcation ? parseFloat(hatimiBifurcation.total) : 0;
    const totalTakhmeen = members.reduce((sum, m) => sum + m.takhmeen, 0);
    const totalReceived = members.reduce((sum, m) => sum + m.received, 0);
    const totalBalance = members.reduce((sum, m) => sum + m.balance, 0);

    const StatCard = ({ icon, title, value, subtitle, color }) => (
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          width: '100px',
          height: '100px',
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          borderRadius: '50%'
        }} />
        <div style={{
          width: '48px',
          height: '48px',
          background: `${color}15`,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          marginBottom: '16px'
        }}>{icon}</div>
        <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px 0' }}>{title}</p>
        <p style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0', fontFamily: "'JetBrains Mono', monospace" }}>{value}</p>
        {subtitle && <p style={{ color, fontSize: '13px', margin: 0 }}>{subtitle}</p>}
      </div>
    );

    return (
      <div className="page-container">
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Dashboard</h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>Overview of Shehrullah 1447 H accounts</p>
        </div>

        <div className="dashboard-grid" style={{ marginBottom: '20px' }}>
          <StatCard icon="💰" title="Total Income" value={formatCurrency(totalIncome)} subtitle={`${receipts.length} receipts`} color="#22c55e" />
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px',
              background: 'radial-gradient(circle, #ef444420 0%, transparent 70%)', borderRadius: '50%'
            }} />
            <div style={{
              width: '48px', height: '48px', background: '#ef444415', borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '16px'
            }}>💸</div>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px 0' }}>Total Expenses</p>
            <p style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalExpenses)}</p>
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 8px 0' }}>{expenses.length} vouchers</p>
            {(kalimiExpenses > 0 || hatimiExpenses > 0) && (
              <div style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', paddingTop: '8px', marginTop: '4px' }}>
                {kalimiExpenses > 0 && <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 2px 0' }}>Kalimi: <span style={{ color: '#fbbf24' }}>{formatCurrency(kalimiExpenses)}</span></p>}
                {hatimiExpenses > 0 && <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 2px 0' }}>Hatmi Markaz: <span style={{ color: '#fbbf24' }}>{formatCurrency(hatimiExpenses)}</span></p>}
                {totalExpenses - kalimiExpenses - hatimiExpenses > 0 && <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: 0 }}>Other: <span style={{ color: '#fbbf24' }}>{formatCurrency(totalExpenses - kalimiExpenses - hatimiExpenses)}</span></p>}
              </div>
            )}
          </div>
          <StatCard icon="🏦" title="Total Available Balance" value={formatCurrency(totalIncome - totalExpenses)} subtitle="Available funds" color="#3b82f6" />
          <StatCard icon="📋" title="Takhmeen Pending" value={formatCurrency(totalBalance)} subtitle={`${members.filter(m => m.balance > 0).length} members`} color="#f59e0b" />
        </div>

        {/* Payment Mode Breakdown */}
        <div className="grid-2-col" style={{ marginBottom: '32px' }}>
          <StatCard icon="💵" title="Cash Receipts" value={formatCurrency(cashReceipts)} subtitle={`${cashReceiptsCount} cash payments`} color="#10b981" />
          <StatCard icon="📱" title="Online Receipts" value={formatCurrency(onlineReceipts)} subtitle={`${onlineReceiptsCount} UPI/Bank/Cheque`} color="#06b6d4" />
        </div>

        {/* Takhmeen Summary */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(201, 162, 39, 0.1) 0%, rgba(201, 162, 39, 0.02) 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(201, 162, 39, 0.2)',
          marginBottom: '24px'
        }}>
          <h3 style={{ color: '#f4d03f', margin: '0 0 20px 0', fontSize: '18px' }}>Takhmeen Collection Progress</h3>
          <div style={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div style={{
                height: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(totalReceived / totalTakhmeen) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #c9a227 0%, #f4d03f 100%)',
                  borderRadius: '6px',
                  transition: 'width 0.5s ease'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Collected: {formatCurrency(totalReceived)}</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Total: {formatCurrency(totalTakhmeen)}</span>
              </div>
            </div>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `conic-gradient(#c9a227 ${(totalReceived / totalTakhmeen) * 360}deg, rgba(255, 255, 255, 0.1) 0deg)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#f4d03f',
                fontWeight: '700',
                fontSize: '16px'
              }}>{Math.round((totalReceived / totalTakhmeen) * 100)}%</div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}>
            <h3 style={{ color: '#fff', margin: '0 0 16px 0', fontSize: '16px' }}>Recent Receipts</h3>
            {receipts.slice(-5).reverse().map(r => (
              <div key={r.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
              }}>
                <div>
                  <p style={{ color: '#fff', margin: 0, fontSize: '14px' }}>{r.name}</p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.4)', margin: 0, fontSize: '12px' }}>{r.receiptNo} • {formatDate(r.date)}</p>
                </div>
                <span style={{ color: '#22c55e', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>+{formatCurrency(r.amount)}</span>
              </div>
            ))}
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}>
            <h3 style={{ color: '#fff', margin: '0 0 16px 0', fontSize: '16px' }}>Pending Takhmeen</h3>
            {members.filter(m => m.balance > 0).slice(0, 5).map(m => (
              <div key={m.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
              }}>
                <div>
                  <p style={{ color: '#fff', margin: 0, fontSize: '14px' }}>{m.name}</p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.4)', margin: 0, fontSize: '12px' }}>ITS: {m.itsId}</p>
                </div>
                <span style={{ color: '#f59e0b', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(m.balance)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============= RECEIPTS COMPONENT =============
  const ReceiptsModule = () => {
    const [showForm, setShowForm] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [memberSearch, setMemberSearch] = useState('');
    const [formData, setFormData] = useState({
      date: new Date().toISOString().split('T')[0],
      itsId: '',
      name: '',
      amount: '',
      mode: 'Cash',
      referenceNumber: '',
      accountId: '',
      bankName: '',
      chequeNumber: '',
      remarks: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Filter members based on search query
    const filteredMembers = members.filter(m =>
      m.itsId.toLowerCase().includes(memberSearch.toLowerCase()) ||
      m.name.toLowerCase().includes(memberSearch.toLowerCase())
    );

    const handleMemberSelect = async (itsId) => {
      const member = members.find(m => m.itsId === itsId);
      if (member) {
        setFormData({ ...formData, itsId, name: member.name });
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');

      try {
        // Get next receipt number from backend
        const nextReceiptData = await api.getNextReceiptNumber();

        // Find member by ITS ID
        const member = members.find(m => m.itsId === formData.itsId);

        // Prepare receipt data for backend
        const receiptData = {
          receipt_no: nextReceiptData.nextNumber,
          receipt_date: formData.date,
          member_id: member?.id || null,
          its_id: formData.itsId,
          name: formData.name,
          amount: parseFloat(formData.amount),
          payment_mode: formData.mode,
          reference_number: formData.referenceNumber || null,
          account_id: formData.accountId || null,
          bank_name: formData.bankName || null,
          cheque_number: formData.chequeNumber || null,
          payment_remarks: formData.remarks || null
        };

        const response = await api.createReceipt(receiptData);

        // Reload data from API
        await loadDataFromAPI();

        // Show the newly created receipt
        const newReceipt = {
          receiptNo: nextReceiptData.nextNumber,
          date: formData.date,
          itsId: formData.itsId,
          name: formData.name,
          amount: parseFloat(formData.amount),
          mode: formData.mode
        };
        setSelectedReceipt(newReceipt);

        setShowForm(false);
        setFormData({
          date: new Date().toISOString().split('T')[0],
          itsId: '',
          name: '',
          amount: '',
          mode: 'Cash',
          referenceNumber: '',
          accountId: '',
          bankName: '',
          chequeNumber: '',
          remarks: ''
        });
      } catch (error) {
        console.error('Failed to create receipt:', error);
        setError(error.message || 'Failed to create receipt. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    // Receipt Print Preview - Half Page Size, Printer Friendly
    const ReceiptPreview = ({ receipt }) => (
      <div className="receipt-print-area" style={{
        background: '#fff',
        padding: '20px 30px',
        maxWidth: '400px',
        margin: '0 auto',
        color: '#000',
        border: '2px solid #000',
        fontSize: '13px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '10px', marginBottom: '12px' }}>
          <h2 style={{ margin: '0 0 2px 0', color: '#000', fontSize: '20px', fontWeight: '700', letterSpacing: '1px' }}>HATIMI HILLS</h2>
          <p style={{ margin: '0 0 2px 0', color: '#000', fontSize: '11px' }}>KALIMI MOHALLA, PUNE</p>
          <p style={{ margin: 0, color: '#000', fontSize: '12px', fontWeight: '600' }}>SHEHRULLAH MUBARAK 1447 H</p>
        </div>

        {/* Receipt No and Date */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '12px' }}>
          <div>
            <span style={{ color: '#000', fontWeight: '600' }}>Receipt No:</span>
            <span style={{ marginLeft: '5px', fontWeight: '700' }}>{receipt.receiptNo}</span>
          </div>
          <div>
            <span style={{ color: '#000', fontWeight: '600' }}>Date:</span>
            <span style={{ marginLeft: '5px' }}>{formatDate(receipt.date)}</span>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }}></div>

        {/* Member Details */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', marginBottom: '6px' }}>
            <span style={{ color: '#000', fontWeight: '600', width: '110px' }}>Received From:</span>
            <span style={{ fontWeight: '600', flex: 1 }}>{receipt.name}</span>
          </div>
          <div style={{ display: 'flex' }}>
            <span style={{ color: '#000', fontWeight: '600', width: '110px' }}>ITS No:</span>
            <span style={{ fontWeight: '600', flex: 1 }}>{receipt.itsId}</span>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }}></div>

        {/* Amount Section - NO DARK BACKGROUND */}
        <div style={{
          border: '2px solid #000',
          padding: '12px',
          marginBottom: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>Amount Received</div>
          <div style={{ fontSize: '26px', fontWeight: '700', margin: '4px 0', letterSpacing: '1px' }}>{formatCurrency(receipt.amount)}</div>
          <div style={{ fontSize: '11px', fontStyle: 'italic', borderTop: '1px solid #000', paddingTop: '6px', marginTop: '6px' }}>
            (Rupees {numberToWords(receipt.amount)} Only)
          </div>
        </div>

        {/* Payment Mode */}
        <div style={{ marginBottom: '12px' }}>
          <span style={{ color: '#000', fontWeight: '600' }}>Payment Mode:</span>
          <span style={{ marginLeft: '8px', fontWeight: '600' }}>{receipt.mode}</span>
        </div>

        {/* Reference ID for online payments */}
        {['UPI', 'Bank Transfer', 'Cheque'].includes(receipt.mode) && receipt.referenceId && (
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#000', fontWeight: '600' }}>Reference ID:</span>
            <span style={{ marginLeft: '8px', fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>{receipt.referenceId}</span>
          </div>
        )}

        {/* Remarks */}
        {receipt.remarks && (
          <div style={{ marginBottom: '12px' }}>
            <span style={{ color: '#000', fontWeight: '600' }}>Remarks:</span>
            <span style={{ marginLeft: '8px', fontSize: '12px' }}>{receipt.remarks}</span>
          </div>
        )}

        {/* Subject to Realization - for non-cash payments */}
        {['UPI', 'Bank Transfer', 'Cheque'].includes(receipt.mode) && (
          <div style={{
            marginTop: '12px',
            marginBottom: '12px',
            padding: '8px',
            border: '2px solid #000',
            textAlign: 'center',
            background: '#f9f9f9'
          }}>
            <span style={{ fontWeight: '700', fontSize: '12px', letterSpacing: '0.5px' }}>SUBJECT TO REALIZATION</span>
          </div>
        )}

        {/* Divider */}
        <div style={{ borderTop: '1px solid #000', margin: '12px 0' }}></div>

        {/* Signature */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
          <div style={{ textAlign: 'center', width: '150px' }}>
            <div style={{ borderTop: '1px solid #000', paddingTop: '4px', fontSize: '10px' }}>
              Authorized Signatory
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div style={{
          textAlign: 'center',
          fontSize: '9px',
          color: '#666',
          marginTop: '15px',
          paddingTop: '8px',
          borderTop: '1px dashed #ccc'
        }}>
          <div style={{ marginBottom: '4px' }}>Thank you for your contribution</div>
          {receipt.createdAt && (
            <div style={{ fontSize: '8px', color: '#999' }}>
              Generated: {new Date(receipt.createdAt).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Receipts</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>Generate and manage payment receipts</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => {
                // Download receipts as CSV
                const sortedReceipts = [...receipts].sort((a, b) => {
                  if (a.createdAt && b.createdAt) {
                    return new Date(a.createdAt) - new Date(b.createdAt);
                  }
                  return a.id - b.id;
                });

                const headers = [
                  'Receipt No', 'Date', 'ITS ID', 'Name', 'Amount', 'Payment Mode',
                  'Reference ID', 'Remarks', 'Created By', 'Created Date'
                ];
                const csvContent = [
                  headers.join(','),
                  ...sortedReceipts.map((r) => [
                    r.receiptNo,
                    formatDate(r.date),
                    r.itsId,
                    `"${r.name}"`,
                    r.amount,
                    r.mode,
                    r.referenceId || '',
                    `"${r.remarks || ''}"`,
                    r.createdBy || '',
                    r.createdAt ? new Date(r.createdAt).toLocaleString('en-IN') : ''
                  ].join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `receipts_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              style={{
                padding: '12px 24px',
                background: 'rgba(59, 130, 246, 0.2)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '10px',
                color: '#3b82f6',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📥 Download CSV
            </button>
            <button
              onClick={() => { setShowForm(true); setSelectedReceipt(null); }}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#0a1628',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              + New Receipt
            </button>
          </div>
        </div>

        {showForm && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div
              className="form-modal"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '20px',
                padding: '32px',
                width: '480px',
                border: '1px solid rgba(201, 162, 39, 0.2)'
              }}
            >
              <h3 style={{ color: '#f4d03f', margin: '0 0 24px 0' }}>Create New Receipt</h3>
              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#fca5a5',
                  fontSize: '13px'
                }}>{error}</div>
              )}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Search Member (ITS ID or Name)</label>
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Type to search by ITS ID or name..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      marginBottom: '12px'
                    }}
                  />
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Select Member {filteredMembers.length !== members.length && `(${filteredMembers.length} of ${members.length})`}</label>
                  <select
                    value={formData.itsId}
                    onChange={(e) => handleMemberSelect(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option style={{ background: '#1e293b', color: '#fff' }} value="">Select member...</option>
                    {filteredMembers.map(m => (
                      <option style={{ background: '#1e293b', color: '#fff' }} key={m.itsId} value={m.itsId}>{m.name} ({m.itsId})</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Enter amount"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Payment Mode</label>
                  <select
                    value={formData.mode}
                    onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option style={{ background: '#1e293b', color: '#fff' }} value="Cash">Cash</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="Bank Transfer">Bank Transfer</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="UPI">UPI</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="Cheque">Cheque</option>
                  </select>
                </div>

                {/* Conditional fields for Bank Transfer and UPI */}
                {(formData.mode === 'Bank Transfer' || formData.mode === 'UPI') && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Reference Number</label>
                    <input
                      type="text"
                      value={formData.referenceNumber}
                      onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                      placeholder="Enter transaction reference number"
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}

                {/* Conditional field for Bank Transfer */}
                {formData.mode === 'Bank Transfer' && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Bank Name</label>
                    <input
                      type="text"
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      placeholder="Enter bank name"
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}

                {/* Conditional field for Cheque */}
                {formData.mode === 'Cheque' && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Cheque Number</label>
                      <input
                        type="text"
                        value={formData.chequeNumber}
                        onChange={(e) => setFormData({ ...formData, chequeNumber: e.target.value })}
                        placeholder="Enter cheque number"
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Bank Name</label>
                      <input
                        type="text"
                        value={formData.bankName}
                        onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                        placeholder="Enter bank name"
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </>
                )}

                {/* Remarks field (optional for all payment modes) */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px' }}>Remarks (Optional)</label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="Enter any additional remarks"
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setError('');
                    }}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: 'pointer',
                      opacity: isSubmitting ? 0.5 : 1
                    }}
                  >Cancel</button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#0a1628',
                      fontWeight: '600',
                      cursor: 'pointer',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  >{isSubmitting ? 'Creating...' : 'Generate Receipt'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {selectedReceipt && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{ position: 'relative' }}>
              <button
                className="no-print"
                onClick={() => setSelectedReceipt(null)}
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: 0,
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >✕ Close</button>
              <ReceiptPreview receipt={selectedReceipt} />
              <div className="no-print" style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  onClick={() => window.print()}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#0a1628',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >🖨️ Print</button>
                <button
                  onClick={() => {
                    // Simple download using print dialog
                    const originalTitle = document.title;
                    document.title = `Receipt_${selectedReceipt.receiptNo}_${formatDate(selectedReceipt.date)}`;
                    window.print();
                    document.title = originalTitle;
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    border: '1px solid rgba(34, 197, 94, 0.4)',
                    borderRadius: '8px',
                    color: '#22c55e',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >⬇️ Download</button>
              </div>
            </div>
          </div>
        )}

        {/* Receipts Table */}
        {isMobile ? (
          /* Mobile Card View */
          <div className="mobile-card-list">
            {receipts.map(r => (
              <div key={r.id} className="mobile-card">
                <div className="mobile-card-header">
                  <div>
                    <div className="mobile-card-title">{r.name}</div>
                    <div className="mobile-card-subtitle">{r.receiptNo} • {formatDate(r.date)}</div>
                  </div>
                  <span style={{
                    padding: '4px 12px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '20px',
                    color: '#60a5fa',
                    fontSize: '11px',
                    whiteSpace: 'nowrap'
                  }}>{r.mode}</span>
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">ITS ID</span>
                    <span className="mobile-card-value" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{r.itsId}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Amount</span>
                    <span className="mobile-card-value" style={{ color: '#22c55e', fontFamily: "'JetBrains Mono', monospace", fontWeight: '600' }}>{formatCurrency(r.amount)}</span>
                  </div>
                  {r.referenceId && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Reference</span>
                      <span className="mobile-card-value" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>{r.referenceId}</span>
                    </div>
                  )}
                  {r.remarks && (
                    <div className="mobile-card-row" style={{ alignItems: 'flex-start' }}>
                      <span className="mobile-card-label">Remarks</span>
                      <span className="mobile-card-value" style={{ textAlign: 'right', fontSize: '12px', whiteSpace: 'normal' }}>{r.remarks}</span>
                    </div>
                  )}
                </div>
                <div className="mobile-card-actions">
                  <button
                    onClick={() => setSelectedReceipt(r)}
                    style={{
                      padding: '12px',
                      background: 'rgba(201, 162, 39, 0.1)',
                      border: '1px solid rgba(201, 162, 39, 0.3)',
                      borderRadius: '8px',
                      color: '#f4d03f',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >👁️ View Receipt</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Desktop Table View */
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(201, 162, 39, 0.1)' }}>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Receipt No.</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Date</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Name</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>ITS ID</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Amount</th>
                  <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Mode</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Reference</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Remark</th>
                  <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <td style={{ padding: '16px', color: '#fff' }}>{r.receiptNo}</td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(r.date)}</td>
                  <td style={{ padding: '16px', color: '#fff' }}>{r.name}</td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.7)', fontFamily: "'JetBrains Mono', monospace" }}>{r.itsId}</td>
                  <td style={{ padding: '16px', color: '#22c55e', fontWeight: '600', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(r.amount)}</td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '20px',
                      color: '#60a5fa',
                      fontSize: '12px'
                    }}>{r.mode}</span>
                  </td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', fontFamily: "'JetBrains Mono', monospace" }}>
                    {r.referenceId || '-'}
                  </td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.remarks || ''}>
                    {r.remarks || '-'}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button
                      onClick={() => setSelectedReceipt(r)}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(201, 162, 39, 0.1)',
                        border: '1px solid rgba(201, 162, 39, 0.3)',
                        borderRadius: '6px',
                        color: '#f4d03f',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >View</button>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============= CASH BOOK COMPONENT =============
  const CashBookModule = () => {
    const totalReceipts = receipts.reduce((sum, r) => sum + r.amount, 0);
    const totalPayments = expenses.reduce((sum, e) => sum + e.amount, 0);
    const closingBalance = accounts.openingBalance + totalReceipts - totalPayments;

    return (
      <div className="page-container">
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Cash Book</h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>Daily cash transactions ledger</p>
        </div>

        {/* Balance Cards */}
        <div className="dashboard-grid" style={{ marginBottom: '24px' }}>
          <div style={{ background: 'rgba(255, 255, 255, 0.03)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 8px 0', textTransform: 'uppercase' }}>Opening Balance</p>
            <p style={{ color: '#fff', fontSize: '24px', fontWeight: '600', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(accounts.openingBalance)}</p>
          </div>
          <div style={{ background: 'rgba(34, 197, 94, 0.05)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 8px 0', textTransform: 'uppercase' }}>Total Receipts</p>
            <p style={{ color: '#22c55e', fontSize: '24px', fontWeight: '600', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>+{formatCurrency(totalReceipts)}</p>
          </div>
          <div style={{ background: 'rgba(239, 68, 68, 0.05)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 8px 0', textTransform: 'uppercase' }}>Total Payments</p>
            <p style={{ color: '#ef4444', fontSize: '24px', fontWeight: '600', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>-{formatCurrency(totalPayments)}</p>
          </div>
          <div style={{ background: 'rgba(201, 162, 39, 0.1)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(201, 162, 39, 0.3)' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 8px 0', textTransform: 'uppercase' }}>Closing Balance</p>
            <p style={{ color: '#f4d03f', fontSize: '24px', fontWeight: '600', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(closingBalance)}</p>
          </div>
        </div>

        {/* Ledger Table */}
        <div className="grid-2-col">
          {/* Receipts Side */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            overflow: 'hidden'
          }}>
            <div style={{ background: 'rgba(34, 197, 94, 0.1)', padding: '16px 20px', borderBottom: '1px solid rgba(34, 197, 94, 0.2)' }}>
              <h3 style={{ margin: 0, color: '#22c55e', fontSize: '16px' }}>📥 RECEIPTS</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>R.No</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <td colSpan={3} style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic' }}>Opening Balance B/F</td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(accounts.openingBalance)}</td>
                </tr>
                {receipts.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={{ padding: '12px', color: '#22c55e', fontSize: '13px' }}>{r.receiptNo}</td>
                    <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>{formatDate(r.date)}</td>
                    <td style={{ padding: '12px', color: '#fff', fontSize: '13px' }}>{r.name}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#22c55e', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(r.amount)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                  <td colSpan={3} style={{ padding: '12px', color: '#22c55e', fontWeight: '600' }}>Total Receipts</td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#22c55e', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalReceipts)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Payments Side */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            overflow: 'hidden'
          }}>
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '16px 20px', borderBottom: '1px solid rgba(239, 68, 68, 0.2)' }}>
              <h3 style={{ margin: 0, color: '#ef4444', fontSize: '16px' }}>📤 PAYMENTS</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>V.No</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>Category</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', textTransform: 'uppercase' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.4)' }}>No expenses recorded yet</td>
                  </tr>
                ) : expenses.map(e => (
                  <tr key={e.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={{ padding: '12px', color: '#ef4444', fontSize: '13px' }}>{e.voucherNo}</td>
                    <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>{formatDate(e.date)}</td>
                    <td style={{ padding: '12px', color: '#fff', fontSize: '13px' }}>{e.category}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(e.amount)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                  <td colSpan={3} style={{ padding: '12px', color: '#ef4444', fontWeight: '600' }}>Total Payments</td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalPayments)}</td>
                </tr>
                <tr style={{ background: 'rgba(201, 162, 39, 0.1)' }}>
                  <td colSpan={3} style={{ padding: '12px', color: '#f4d03f', fontWeight: '600' }}>Closing Balance C/F</td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(closingBalance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ============= TAKHMEEN MODULE =============
  const TakhmeenModule = () => {
    const [showForm, setShowForm] = useState(false);
    const [editMember, setEditMember] = useState(null);
    const [formData, setFormData] = useState({
      itsId: '', name: '', maleAdults: 0, femaleAdults: 0, children: 0, takhmeen: 0,
      // Additional fields from external API - mapped to DB schema
      fullNiyaz: 0, halfNiyaz: 0, zabihat: 0, salawaat: 0,
      niyazRaat: '', chairNumber: '', izanCardNumber: '', pirsaCount: 0,
      phone: '', email: '', address: ''
    });

    const [loadingTakhmeenData, setLoadingTakhmeenData] = useState(false);
    const [familyData, setFamilyData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [lastLoadedItsId, setLastLoadedItsId] = useState(''); // Track the ITS ID that was last loaded
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const membersPerPage = 20;

    // Filter members based on search query
    const filteredMembers = members.filter(m =>
      m.itsId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Pagination calculations
    const totalPages = Math.ceil(filteredMembers.length / membersPerPage);
    const startIndex = (currentPage - 1) * membersPerPage;
    const endIndex = startIndex + membersPerPage;
    const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

    const totalTakhmeen = members.reduce((sum, m) => sum + m.takhmeen, 0);
    const totalReceived = members.reduce((sum, m) => sum + m.received, 0);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');

      try {
        // Prepare data for backend API
        const memberData = {
          its_id: formData.itsId,
          name: formData.name,
          male_adults: parseInt(formData.maleAdults) || 0,
          female_adults: parseInt(formData.femaleAdults) || 0,
          children: parseInt(formData.children) || 0,
          takhmeen_amount: parseFloat(formData.takhmeen) || 0,
          full_niyaz: parseFloat(formData.fullNiyaz) || 0,
          half_niyaz: parseFloat(formData.halfNiyaz) || 0,
          zabihat: parseFloat(formData.zabihat) || 0,
          salawaat: parseFloat(formData.salawaat) || 0,
          niyaz_raat: formData.niyazRaat || null,
          chair_number: formData.chairNumber || null,
          izan_card_number: formData.izanCardNumber || null,
          pirsa_count: parseInt(formData.pirsaCount) || 0,
          phone: formData.phone || null,
          email: formData.email || null,
          address: formData.address || null
        };

        if (editMember) {
          // Update existing member
          await api.updateMember(editMember.id, memberData);
        } else {
          // Create new member
          await api.createMember(memberData);
        }

        // Reload members list from API
        await loadDataFromAPI();

        // Reset form and close
        setShowForm(false);
        setEditMember(null);
        setFormData({
          itsId: '', name: '', maleAdults: 0, femaleAdults: 0, children: 0, takhmeen: 0,
          fullNiyaz: 0, halfNiyaz: 0, zabihat: 0, salawaat: 0,
          niyazRaat: '', chairNumber: '', izanCardNumber: '', pirsaCount: 0,
          phone: '', email: '', address: ''
        });
        setFamilyData([]);
        setLastLoadedItsId(''); // Reset tracked ITS ID
      } catch (error) {
        console.error(`Failed to ${editMember ? 'update' : 'create'} member:`, error);
        setError(error.message || `Failed to ${editMember ? 'update' : 'create'} member. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    };

    // Handle ITS ID change and fetch external API data
    const handleItsIdChange = async (e) => {
      const itsId = e.target.value;
      setFormData({ ...formData, itsId });

      // If ITS ID is valid (8 digits), fetch external data
      if (itsId.length === 8 && /^\d+$/.test(itsId)) {
        setLoadingTakhmeenData(true);
        try {
          // Fetch both Takhmeen and Family data
          const [takhmeenResponse, familyResponse] = await Promise.all([
            api.fetchTakhmeenData(itsId).catch(() => null),
            api.fetchFamilyData(itsId).catch(() => null)
          ]);

          if (takhmeenResponse && takhmeenResponse.data) {
            const tData = takhmeenResponse.data;
            // Find HOF from family data - where its_id matches the entered ITS ID
            let hofName = '';
            let hofEmail = '';
            let hofAddress = '';

            if (familyResponse && familyResponse.data) {
              const hofMember = familyResponse.data.find(member => member.its_id.toString() === itsId);
              if (hofMember) {
                hofName = hofMember.full_name;
                hofEmail = hofMember.email || '';
                hofAddress = hofMember.address || '';
              }
              setFamilyData(familyResponse.data);
            }

            // Check if ITS ID has changed from the last loaded one
            const isNewItsId = itsId !== lastLoadedItsId;

            // Auto-fill form with API data
            // If it's a new ITS ID, always replace all data
            // If it's the same ITS ID, preserve manually edited fields
            setFormData(prev => ({
              ...prev,
              itsId,
              name: isNewItsId ? hofName : (prev.name || hofName),
              maleAdults: familyResponse?.data?.filter(m => m.gender === 'Male' && m.age >= 18).length || 0,
              femaleAdults: familyResponse?.data?.filter(m => m.gender === 'Female' && m.age >= 18).length || 0,
              children: familyResponse?.data?.filter(m => m.age < 18).length || 0,
              takhmeen: tData.total_hub || tData.family_hub || 0,
              fullNiyaz: tData.niyaz_type === 'full' ? (tData.niyaz_hub || 0) : 0,
              halfNiyaz: tData.niyaz_type === 'half' ? (tData.niyaz_hub || 0) : 0,
              zabihat: tData.zabihat_count || 0,
              salawaat: isNewItsId ? 0 : (prev.salawaat || 0), // Preserve manual entry for same ITS ID
              niyazRaat: isNewItsId ? '' : (prev.niyazRaat || ''), // Don't auto-fill, let user enter manually
              chairNumber: tData.chair_count ? `${tData.chair_count}` : '',
              pirsaCount: tData.pirsa_count || 0,
              izanCardNumber: isNewItsId ? '' : prev.izanCardNumber, // Preserve manual entry for same ITS ID
              phone: isNewItsId ? (tData.whatsapp || '') : (prev.phone || tData.whatsapp || ''),
              email: isNewItsId ? hofEmail : (prev.email || hofEmail),
              address: isNewItsId ? hofAddress : (prev.address || hofAddress)
            }));

            // Update the last loaded ITS ID
            setLastLoadedItsId(itsId);
          }
        } catch (error) {
          console.error('Error fetching external data:', error);
        }
        setLoadingTakhmeenData(false);
      }
    };

    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Takhmeen Management</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>Member contributions and balances</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => {
                // Download report as CSV with ALL fields
                // Sort by creation date (insertion order)
                const sortedMembers = [...members].sort((a, b) => {
                  // Sort by created date if available, otherwise by ID
                  if (a.createdAt && b.createdAt) {
                    return new Date(a.createdAt) - new Date(b.createdAt);
                  }
                  return a.id - b.id;
                });

                const headers = [
                  'DB ID', 'ITS ID', 'Name', 'Male Adults', 'Female Adults', 'Children', 'Total Count',
                  'Takhmeen Amount', 'Received', 'Balance', 'Status',
                  'Full Niyaz', 'Half Niyaz', 'Zabihat', 'Salawaat',
                  'Niyaz Raat', 'Chair Number', 'Izan Card Number', 'Pirsa Count', 'Phone', 'Created Date'
                ];
                const csvContent = [
                  headers.join(','),
                  ...sortedMembers.map((m) => [
                    m.id,
                    m.itsId,
                    `"${m.name}"`,
                    m.maleAdults,
                    m.femaleAdults,
                    m.children,
                    m.totalCount,
                    m.takhmeen,
                    m.received,
                    m.balance,
                    m.balance === 0 ? 'PAID' : 'PENDING',
                    m.fullNiyaz || 0,
                    m.halfNiyaz || 0,
                    m.zabihat || 0,
                    m.salawaat || 0,
                    `"${m.niyazRaat || ''}"`,
                    m.chairNumber || '',
                    m.izanCardNumber || '',
                    m.pirsaCount || 0,
                    m.phone || '',
                    m.createdAt ? new Date(m.createdAt).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''
                  ].join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `takhmeen_report_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              style={{
                padding: '12px 24px',
                background: 'rgba(59, 130, 246, 0.2)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '10px',
                color: '#3b82f6',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >📥 Download Report</button>
            {currentUser.role === 'admin' && (
              <button
                onClick={() => {
                  setEditMember(null);
                  setFormData({
                    itsId: '', name: '', maleAdults: 0, femaleAdults: 0, children: 0, takhmeen: 0,
                    fullNiyaz: 0, halfNiyaz: 0, zabihat: 0, salawaat: 0,
                    niyazRaat: '', chairNumber: '', izanCardNumber: '', pirsaCount: 0,
                    phone: '', email: '', address: ''
                  });
                  setFamilyData([]);
                  setLastLoadedItsId(''); // Reset tracked ITS ID for fresh form
                  setShowForm(true);
                }}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#0a1628',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >+ Add Member</button>
            )}
          </div>
        </div>

        {/* Search Box */}
        <div style={{ marginBottom: '24px' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1); // Reset to first page on new search
            }}
            placeholder="🔍 Search by ITS ID or Name..."
            style={{
              width: '100%',
              padding: '14px 20px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '15px',
              boxSizing: 'border-box'
            }}
          />
          {searchQuery && (
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px', marginTop: '8px', marginBottom: 0 }}>
              Found {filteredMembers.length} of {members.length} members
            </p>
          )}
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'rgba(201, 162, 39, 0.1)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(201, 162, 39, 0.2)' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 8px 0' }}>Total Takhmeen</p>
            <p style={{ color: '#f4d03f', fontSize: '28px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalTakhmeen)}</p>
          </div>
          <div style={{ background: 'rgba(34, 197, 94, 0.1)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 8px 0' }}>Amount Received</p>
            <p style={{ color: '#22c55e', fontSize: '28px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalReceived)}</p>
          </div>
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', margin: '0 0 8px 0' }}>Pending Balance</p>
            <p style={{ color: '#ef4444', fontSize: '28px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalTakhmeen - totalReceived)}</p>
          </div>
        </div>

        {showForm && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div
              className="form-modal"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '20px',
                padding: '32px',
                width: '700px',
                maxHeight: '90vh',
                overflow: 'auto',
                border: '1px solid rgba(201, 162, 39, 0.2)'
              }}
            >
              <h3 style={{ color: '#f4d03f', margin: '0 0 24px 0' }}>{editMember ? 'Edit Member' : 'Add New Member'}</h3>
              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#fca5a5',
                  fontSize: '13px'
                }}>{error}</div>
              )}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ color: '#f4d03f', fontSize: '14px', marginBottom: '12px', textTransform: 'uppercase' }}>Basic Information</h4>
                  <div className="form-grid-2" style={{ gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>ITS ID *</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          value={formData.itsId}
                          onChange={handleItsIdChange}
                          required
                          placeholder="Enter ITS ID to auto-fill data"
                          style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                        />
                        {loadingTakhmeenData && (
                          <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#f4d03f', fontSize: '12px' }}>
                            Loading...
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Full Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Male Adults</label>
                      <input
                        type="number"
                        value={formData.maleAdults}
                        onChange={(e) => setFormData({ ...formData, maleAdults: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Female Adults</label>
                      <input
                        type="number"
                        value={formData.femaleAdults}
                        onChange={(e) => setFormData({ ...formData, femaleAdults: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Children</label>
                      <input
                        type="number"
                        value={formData.children}
                        onChange={(e) => setFormData({ ...formData, children: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Takhmeen Amount</label>
                      <input
                        type="number"
                        value={formData.takhmeen}
                        onChange={(e) => setFormData({ ...formData, takhmeen: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ color: '#f4d03f', fontSize: '14px', marginBottom: '12px', textTransform: 'uppercase' }}>Niyaz & Additional Details</h4>
                  <div className="form-grid-2" style={{ gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Full Niyaz Amount</label>
                      <input
                        type="number"
                        value={formData.fullNiyaz}
                        onChange={(e) => setFormData({ ...formData, fullNiyaz: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Half Niyaz Amount</label>
                      <input
                        type="number"
                        value={formData.halfNiyaz}
                        onChange={(e) => setFormData({ ...formData, halfNiyaz: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Zabihat</label>
                      <input
                        type="number"
                        value={formData.zabihat}
                        onChange={(e) => setFormData({ ...formData, zabihat: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Salawaat</label>
                      <input
                        type="number"
                        value={formData.salawaat}
                        onChange={(e) => setFormData({ ...formData, salawaat: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Niyaz Raat</label>
                      <input
                        type="text"
                        value={formData.niyazRaat}
                        onChange={(e) => setFormData({ ...formData, niyazRaat: e.target.value })}
                        placeholder="e.g., full, half"
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Chair Number</label>
                      <input
                        type="text"
                        value={formData.chairNumber}
                        onChange={(e) => setFormData({ ...formData, chairNumber: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Izan Card Number</label>
                      <input
                        type="text"
                        value={formData.izanCardNumber}
                        onChange={(e) => setFormData({ ...formData, izanCardNumber: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Pirsa Count</label>
                      <input
                        type="number"
                        value={formData.pirsaCount}
                        onChange={(e) => setFormData({ ...formData, pirsaCount: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ color: '#f4d03f', fontSize: '14px', marginBottom: '12px', textTransform: 'uppercase' }}>Contact Information</h4>
                  <div className="form-grid-2" style={{ gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Phone</label>
                      <input
                        type="text"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Address</label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        rows="2"
                        style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                    </div>
                  </div>
                </div>

                {familyData.length > 0 && (
                  <div style={{ marginBottom: '24px', padding: '16px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    <h4 style={{ color: '#3b82f6', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>Family Members ({familyData.length})</h4>
                    <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)' }}>
                      {familyData.map((member, idx) => (
                        <div key={idx}>{member.full_name} - {member.gender} ({member.age} years)</div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button type="button" onClick={() => {
                    setShowForm(false);
                    setEditMember(null);
                    setFormData({
                      itsId: '', name: '', maleAdults: 0, femaleAdults: 0, children: 0, takhmeen: 0,
                      fullNiyaz: 0, halfNiyaz: 0, zabihat: 0, salawaat: 0,
                      niyazRaat: '', chairNumber: '', izanCardNumber: '', pirsaCount: 0,
                      phone: '', email: '', address: ''
                    });
                    setFamilyData([]);
                    setLastLoadedItsId(''); // Reset tracked ITS ID
                  }} disabled={isSubmitting} style={{ flex: 1, padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', cursor: 'pointer', opacity: isSubmitting ? 0.5 : 1 }}>Cancel</button>
                  <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)', border: 'none', borderRadius: '8px', color: '#0a1628', fontWeight: '600', cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1 }}>
                    {isSubmitting ? (editMember ? 'Updating...' : 'Adding...') : (editMember ? 'Update Member' : 'Add Member')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Members Table */}
        {isMobile ? (
          /* Mobile Card View */
          <div className="mobile-card-list">
            {paginatedMembers.map((m, idx) => (
              <div key={m.id} className="mobile-card">
                <div className="mobile-card-header">
                  <div>
                    <div className="mobile-card-title">{m.name}</div>
                    <div className="mobile-card-subtitle">ITS: {m.itsId}</div>
                  </div>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: m.balance === 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                    color: m.balance === 0 ? '#22c55e' : '#f59e0b',
                    border: `1px solid ${m.balance === 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                  }}>{m.balance === 0 ? 'PAID' : 'PENDING'}</span>
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Family</span>
                    <span className="mobile-card-value">{m.maleAdults}M + {m.femaleAdults}F + {m.children}C = {m.totalCount}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Takhmeen</span>
                    <span className="mobile-card-value" style={{ color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(m.takhmeen)}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Received</span>
                    <span className="mobile-card-value" style={{ color: '#22c55e', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(m.received)}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Balance</span>
                    <span className="mobile-card-value" style={{ color: m.balance > 0 ? '#ef4444' : '#22c55e', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(m.balance)}</span>
                  </div>
                  {m.zabihat > 0 && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Zabihat</span>
                      <span className="mobile-card-value">{m.zabihat}</span>
                    </div>
                  )}
                  {m.pirsaCount > 0 && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Pirsa</span>
                      <span className="mobile-card-value">{m.pirsaCount}</span>
                    </div>
                  )}
                  {m.chairNumber && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Chair No.</span>
                      <span className="mobile-card-value">{m.chairNumber}</span>
                    </div>
                  )}
                </div>
                {currentUser.role === 'admin' && (
                  <div className="mobile-card-actions">
                    <button
                      onClick={() => {
                        setEditMember(m);
                        setFormData({
                          itsId: m.itsId,
                          name: m.name,
                          maleAdults: m.maleAdults || 0,
                          femaleAdults: m.femaleAdults || 0,
                          children: m.children || 0,
                          takhmeen: m.takhmeen || 0,
                          fullNiyaz: m.fullNiyaz || 0,
                          halfNiyaz: m.halfNiyaz || 0,
                          zabihat: m.zabihat || 0,
                          salawaat: m.salawaat || 0,
                          niyazRaat: m.niyazRaat || '',
                          chairNumber: m.chairNumber || '',
                          izanCardNumber: m.izanCardNumber || '',
                          pirsaCount: m.pirsaCount || 0,
                          phone: m.phone || '',
                          email: m.email || '',
                          address: m.address || ''
                        });
                        setShowForm(true);
                      }}
                      style={{
                        padding: '12px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: '8px',
                        color: '#3b82f6',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >✏️ Edit Member</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Desktop Table View */
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(201, 162, 39, 0.1)' }}>
                <th style={{ padding: '14px', textAlign: 'left', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Sr.</th>
                <th style={{ padding: '14px', textAlign: 'left', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>ITS ID</th>
                <th style={{ padding: '14px', textAlign: 'left', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Name</th>
                <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Family</th>
                <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Zabihat</th>
                <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Pirsa</th>
                <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Chair No.</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Takhmeen</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Received</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Balance</th>
                <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Status</th>
                {currentUser.role === 'admin' && (
                  <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedMembers.map((m, idx) => (
                <tr key={m.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <td style={{ padding: '14px', color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>{startIndex + idx + 1}</td>
                  <td style={{ padding: '14px', color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontSize: '13px' }}>{m.itsId}</td>
                  <td style={{ padding: '14px', color: '#fff', fontSize: '13px' }}>{m.name}</td>
                  <td style={{ padding: '14px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>
                    {m.maleAdults}M + {m.femaleAdults}F + {m.children}C = {m.totalCount}
                  </td>
                  <td style={{ padding: '14px', textAlign: 'center', color: '#fff', fontSize: '13px' }}>{m.zabihat || 0}</td>
                  <td style={{ padding: '14px', textAlign: 'center', color: '#fff', fontSize: '13px' }}>{m.pirsaCount || 0}</td>
                  <td style={{ padding: '14px', textAlign: 'center', color: '#fff', fontSize: '13px' }}>{m.chairNumber || '-'}</td>
                  <td style={{ padding: '14px', textAlign: 'right', color: '#fff', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(m.takhmeen)}</td>
                  <td style={{ padding: '14px', textAlign: 'right', color: '#22c55e', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(m.received)}</td>
                  <td style={{ padding: '14px', textAlign: 'right', color: m.balance > 0 ? '#ef4444' : '#22c55e', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(m.balance)}</td>
                  <td style={{ padding: '14px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: m.balance === 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                      color: m.balance === 0 ? '#22c55e' : '#f59e0b',
                      border: `1px solid ${m.balance === 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`
                    }}>{m.balance === 0 ? 'PAID' : 'PENDING'}</span>
                  </td>
                  {currentUser.role === 'admin' && (
                    <td style={{ padding: '14px', textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          setEditMember(m);
                          setFormData({
                            itsId: m.itsId,
                            name: m.name,
                            maleAdults: m.maleAdults || 0,
                            femaleAdults: m.femaleAdults || 0,
                            children: m.children || 0,
                            takhmeen: m.takhmeen || 0,
                            fullNiyaz: m.fullNiyaz || 0,
                            halfNiyaz: m.halfNiyaz || 0,
                            zabihat: m.zabihat || 0,
                            salawaat: m.salawaat || 0,
                            niyazRaat: m.niyazRaat || '',
                            chairNumber: m.chairNumber || '',
                            izanCardNumber: m.izanCardNumber || '',
                            pirsaCount: m.pirsaCount || 0,
                            phone: m.phone || '',
                            email: m.email || '',
                            address: m.address || ''
                          });
                          setShowForm(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: 'rgba(59, 130, 246, 0.2)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: '6px',
                          color: '#3b82f6',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >✏️ Edit</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination Controls */}
        {filteredMembers.length > membersPerPage && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '24px',
            padding: '20px',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}>
            <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredMembers.length)} of {filteredMembers.length} members
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 16px',
                  background: currentPage === 1 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(201, 162, 39, 0.1)',
                  border: `1px solid ${currentPage === 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(201, 162, 39, 0.3)'}`,
                  borderRadius: '8px',
                  color: currentPage === 1 ? 'rgba(255, 255, 255, 0.3)' : '#f4d03f',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >← Previous</button>

              <div style={{ display: 'flex', gap: '4px' }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1) ? (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: '8px 12px',
                        background: page === currentPage ? 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)' : 'rgba(255, 255, 255, 0.05)',
                        border: `1px solid ${page === currentPage ? '#f4d03f' : 'rgba(255, 255, 255, 0.1)'}`,
                        borderRadius: '8px',
                        color: page === currentPage ? '#0a1628' : '#fff',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: page === currentPage ? '700' : '500'
                      }}
                    >{page}</button>
                  ) : (page === currentPage - 2 || page === currentPage + 2) ? (
                    <span key={page} style={{ padding: '8px 4px', color: 'rgba(255, 255, 255, 0.3)' }}>...</span>
                  ) : null
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 16px',
                  background: currentPage === totalPages ? 'rgba(255, 255, 255, 0.05)' : 'rgba(201, 162, 39, 0.1)',
                  border: `1px solid ${currentPage === totalPages ? 'rgba(255, 255, 255, 0.1)' : 'rgba(201, 162, 39, 0.3)'}`,
                  borderRadius: '8px',
                  color: currentPage === totalPages ? 'rgba(255, 255, 255, 0.3)' : '#f4d03f',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >Next →</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============= EXPENSES MODULE =============
  const ExpensesModule = () => {
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
      date: new Date().toISOString().split('T')[0],
      category: '',
      description: '',
      amount: '',
      paidTo: '',
      paymentFrom: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [availableCategories, setAvailableCategories] = useState([]);
    const [editingExpense, setEditingExpense] = useState(null);
    const [editFormData, setEditFormData] = useState({
      date: '',
      category: '',
      description: '',
      amount: '',
      paidTo: '',
      paymentFrom: ''
    });

    // Load categories from API
    React.useEffect(() => {
      const loadCategories = async () => {
        try {
          const categoryData = await api.getExpenseCategories();
          setAvailableCategories(categoryData);
        } catch (error) {
          console.error('Failed to load categories:', error);
          // Fallback to hardcoded categories
          setAvailableCategories(expenseCategories.map((name, index) => ({
            id: index + 1,
            name,
            is_active: true,
            parent_id: null
          })));
        }
      };
      loadCategories();
    }, []);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');

      try {
        // Find category_id from selected category name
        const selectedCat = availableCategories.find(c => c.name === formData.category);

        // Prepare expense data for backend
        const expenseData = {
          expense_date: formData.date,
          category_id: selectedCat ? selectedCat.id : null,
          category_name: formData.category,
          description: formData.description,
          amount: parseFloat(formData.amount),
          paid_to: formData.paidTo,
          payment_from: formData.paymentFrom,
          payment_mode: 'Cash'
        };

        await api.createExpense(expenseData);

        // Reload data from API
        await loadDataFromAPI();

        setShowForm(false);
        setFormData({ date: new Date().toISOString().split('T')[0], category: '', description: '', amount: '', paidTo: '', paymentFrom: '' });
      } catch (error) {
        console.error('Failed to create expense:', error);
        setError(error.message || 'Failed to create expense. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    const downloadExpensesExcel = () => {
      if (expenses.length === 0) {
        alert('No expenses to export');
        return;
      }

      const wb = XLSX.utils.book_new();
      const wsData = [];

      // Title
      wsData.push(['HATIMI HILLS MARKAZ - Expense Report']);
      wsData.push(['Generated on: ' + formatDate(new Date())]);
      wsData.push([]);

      // Header
      wsData.push(['Sr.', 'Voucher No.', 'Date', 'Category', 'Description', 'Paid To', 'Payment From', 'Amount (₹)']);

      // Category-wise summary
      const categoryTotals = {};

      // Items
      expenses.forEach((e, idx) => {
        wsData.push([
          idx + 1,
          e.voucherNo,
          formatDate(e.date),
          e.category,
          e.description || '',
          e.paidTo || '',
          e.paymentFrom || '',
          parseFloat(e.amount)
        ]);
        categoryTotals[e.category] = (categoryTotals[e.category] || 0) + parseFloat(e.amount);
      });

      // Total row
      const totalAmount = expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);
      wsData.push([]);
      wsData.push(['', '', '', '', '', '', 'Total Expenses:', totalAmount]);

      // Summary section
      wsData.push([]);
      wsData.push([]);
      wsData.push(['CATEGORY-WISE SUMMARY']);
      wsData.push(['Category', 'Amount (₹)', 'No. of Entries']);

      Object.entries(categoryTotals)
        .sort((a, b) => b[1] - a[1])
        .forEach(([cat, amount]) => {
          const count = expenses.filter(e => e.category === cat).length;
          wsData.push([cat, amount, count]);
        });
      wsData.push(['Total', totalAmount, expenses.length]);

      // Payment source summary
      const sourceTotals = {};
      expenses.forEach(e => {
        const src = e.paymentFrom || 'Not Specified';
        sourceTotals[src] = (sourceTotals[src] || 0) + parseFloat(e.amount);
      });
      wsData.push([]);
      wsData.push(['PAYMENT SOURCE SUMMARY']);
      wsData.push(['Source', 'Amount (₹)', 'No. of Entries']);
      Object.entries(sourceTotals)
        .sort((a, b) => b[1] - a[1])
        .forEach(([src, amount]) => {
          const count = expenses.filter(e => (e.paymentFrom || 'Not Specified') === src).length;
          wsData.push([src, amount, count]);
        });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Column widths
      ws['!cols'] = [
        { wch: 5 },   // Sr
        { wch: 14 },  // Voucher
        { wch: 14 },  // Date
        { wch: 30 },  // Category
        { wch: 25 },  // Description
        { wch: 20 },  // Paid To
        { wch: 16 },  // Payment From
        { wch: 14 },  // Amount
      ];

      // Merge title
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];

      XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
      XLSX.writeFile(wb, `Expenses_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleEditExpense = (expense) => {
      setEditingExpense(expense);
      setEditFormData({
        date: expense.date ? new Date(expense.date).toISOString().split('T')[0] : '',
        category: expense.category || '',
        description: expense.description || '',
        amount: expense.amount || '',
        paidTo: expense.paidTo || '',
        paymentFrom: expense.paymentFrom || ''
      });
      setError('');
    };

    const handleUpdateExpense = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');

      try {
        const selectedCat = availableCategories.find(c => c.name === editFormData.category);

        const expenseData = {
          expense_date: editFormData.date,
          category_id: selectedCat ? selectedCat.id : null,
          category_name: editFormData.category,
          description: editFormData.description,
          amount: parseFloat(editFormData.amount),
          paid_to: editFormData.paidTo,
          payment_from: editFormData.paymentFrom,
          payment_mode: 'Cash'
        };

        await api.updateExpense(editingExpense.id, expenseData);
        await loadDataFromAPI();
        setEditingExpense(null);
      } catch (error) {
        console.error('Failed to update expense:', error);
        setError(error.message || 'Failed to update expense. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    const expensesByCategory = expenseCategories.map(cat => ({
      category: cat,
      total: expenses.filter(e => e.category === cat).reduce((sum, e) => sum + e.amount, 0)
    })).filter(c => c.total > 0);

    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Expenses</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>Track and manage all expenditures</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={downloadExpensesExcel}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >Export Excel</button>
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >+ Add Expense</button>
          </div>
        </div>

        {showForm && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div
              className="form-modal"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '20px',
                padding: '32px',
                width: '480px',
                border: '1px solid rgba(239, 68, 68, 0.2)'
              }}
            >
              <h3 style={{ color: '#ef4444', margin: '0 0 24px 0' }}>Record New Expense</h3>
              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#fca5a5',
                  fontSize: '13px'
                }}>{error}</div>
              )}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Date</label>
                  <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Category</label>
                  <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="">Select category...</option>
                    {availableCategories
                      .filter(cat => cat.is_active && !cat.parent_id)
                      .map(cat => {
                        const subcats = availableCategories.filter(sub => sub.parent_id === cat.id && sub.is_active);
                        return (
                          <React.Fragment key={cat.id}>
                            <option style={{ background: '#1e293b', color: '#fff', fontWeight: '600' }} value={cat.name}>{cat.name}</option>
                            {subcats.map(sub => (
                              <option style={{ background: '#1e293b', color: '#fff', paddingLeft: '20px' }} key={sub.id} value={sub.name}>
                                └─ {sub.name}
                              </option>
                            ))}
                          </React.Fragment>
                        );
                      })}
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Description</label>
                  <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description" style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Amount (₹)</label>
                  <input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="Enter amount" style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Paid To</label>
                  <input type="text" value={formData.paidTo} onChange={(e) => setFormData({ ...formData, paidTo: e.target.value })} placeholder="Vendor/Person name" style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Payment From</label>
                  <select value={formData.paymentFrom} onChange={(e) => setFormData({ ...formData, paymentFrom: e.target.value })} style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="">Select payment source...</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="Kalimi">Kalimi</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="Hatmi Markaz">Hatmi Markaz</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={() => { setShowForm(false); setError(''); }} disabled={isSubmitting} style={{ flex: 1, padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', cursor: 'pointer', opacity: isSubmitting ? 0.5 : 1 }}>Cancel</button>
                  <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '600', cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1 }}>{isSubmitting ? 'Recording...' : 'Record Expense'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Expense Modal */}
        {editingExpense && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div
              className="form-modal"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '20px',
                padding: '32px',
                width: '480px',
                border: '1px solid rgba(239, 68, 68, 0.2)'
              }}
            >
              <h3 style={{ color: '#ef4444', margin: '0 0 24px 0' }}>Edit Expense - {editingExpense.voucherNo}</h3>
              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#fca5a5',
                  fontSize: '13px'
                }}>{error}</div>
              )}
              <form onSubmit={handleUpdateExpense}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Date</label>
                  <input type="date" value={editFormData.date} onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })} style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Category</label>
                  <select value={editFormData.category} onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })} style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="">Select category...</option>
                    {availableCategories
                      .filter(cat => cat.is_active && !cat.parent_id)
                      .map(cat => {
                        const subcats = availableCategories.filter(sub => sub.parent_id === cat.id && sub.is_active);
                        return (
                          <React.Fragment key={cat.id}>
                            <option style={{ background: '#1e293b', color: '#fff', fontWeight: '600' }} value={cat.name}>{cat.name}</option>
                            {subcats.map(sub => (
                              <option style={{ background: '#1e293b', color: '#fff', paddingLeft: '20px' }} key={sub.id} value={sub.name}>
                                └─ {sub.name}
                              </option>
                            ))}
                          </React.Fragment>
                        );
                      })}
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Description</label>
                  <input type="text" value={editFormData.description} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} placeholder="Brief description" style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Amount (₹)</label>
                  <input type="number" value={editFormData.amount} onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })} placeholder="Enter amount" style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Paid To</label>
                  <input type="text" value={editFormData.paidTo} onChange={(e) => setEditFormData({ ...editFormData, paidTo: e.target.value })} placeholder="Vendor/Person name" style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }} />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Payment From</label>
                  <select value={editFormData.paymentFrom} onChange={(e) => setEditFormData({ ...editFormData, paymentFrom: e.target.value })} style={{ width: '100%', padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', boxSizing: 'border-box' }}>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="">Select payment source...</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="Kalimi">Kalimi</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="Hatmi Markaz">Hatmi Markaz</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={() => { setEditingExpense(null); setError(''); }} disabled={isSubmitting} style={{ flex: 1, padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', cursor: 'pointer', opacity: isSubmitting ? 0.5 : 1 }}>Cancel</button>
                  <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '600', cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1 }}>{isSubmitting ? 'Updating...' : 'Update Expense'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Summary by Category */}
        {expensesByCategory.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
            {expensesByCategory.map(c => (
              <div key={c.category} style={{ background: 'rgba(239, 68, 68, 0.05)', borderRadius: '10px', padding: '16px', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '11px', margin: '0 0 4px 0', textTransform: 'uppercase' }}>{c.category}</p>
                <p style={{ color: '#ef4444', fontSize: '18px', fontWeight: '600', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(c.total)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Expenses Table */}
        {isMobile ? (
          /* Mobile Card View */
          expenses.length === 0 ? (
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              padding: '60px 20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💸</div>
              <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.4)' }}>No expenses recorded yet</p>
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: 'rgba(255, 255, 255, 0.3)' }}>Click "Add Expense" to record your first expense</p>
            </div>
          ) : (
            <div>
              <div className="mobile-card-list">
                {expenses.map(e => (
                  <div key={e.id} className="mobile-card">
                    <div className="mobile-card-header">
                      <div>
                        <div className="mobile-card-title">{e.category}</div>
                        <div className="mobile-card-subtitle">{e.voucherNo} • {formatDate(e.date)}</div>
                      </div>
                      <span className="mobile-card-value" style={{ color: '#ef4444', fontFamily: "'JetBrains Mono', monospace", fontWeight: '600' }}>{formatCurrency(e.amount)}</span>
                    </div>
                    <div className="mobile-card-body">
                      {e.description && (
                        <div className="mobile-card-row" style={{ alignItems: 'flex-start' }}>
                          <span className="mobile-card-label">Description</span>
                          <span className="mobile-card-value" style={{ textAlign: 'right', whiteSpace: 'normal' }}>{e.description}</span>
                        </div>
                      )}
                      {e.paidTo && (
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Paid To</span>
                          <span className="mobile-card-value">{e.paidTo}</span>
                        </div>
                      )}
                      {e.paymentFrom && (
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Payment From</span>
                          <span className="mobile-card-value">{e.paymentFrom}</span>
                        </div>
                      )}
                      <div className="mobile-card-row" style={{ justifyContent: 'flex-end', paddingTop: '8px' }}>
                        <button onClick={() => handleEditExpense(e)} style={{ background: 'transparent', border: '1px solid rgba(201, 162, 39, 0.3)', borderRadius: '4px', color: '#c9a227', padding: '6px 16px', fontSize: '12px', cursor: 'pointer' }}>Edit</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}>
                <span style={{ color: '#ef4444', fontWeight: '600', fontSize: '14px' }}>Total Expenses</span>
                <span style={{ color: '#ef4444', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace", fontSize: '18px' }}>
                  {formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0))}
                </span>
              </div>
            </div>
          )
        ) : (
          /* Desktop Table View */
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Voucher No.</th>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Category</th>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Description</th>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Paid To</th>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Payment From</th>
                  <th style={{ padding: '14px', textAlign: 'right', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '14px', textAlign: 'center', color: '#ef4444', fontSize: '11px', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '60px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.4)' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>💸</div>
                    <p style={{ margin: 0 }}>No expenses recorded yet</p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>Click "Add Expense" to record your first expense</p>
                  </td>
                </tr>
              ) : expenses.map(e => (
                <tr key={e.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <td style={{ padding: '14px', color: '#ef4444', fontSize: '13px' }}>{e.voucherNo}</td>
                  <td style={{ padding: '14px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>{formatDate(e.date)}</td>
                  <td style={{ padding: '14px', color: '#fff', fontSize: '13px' }}>{e.category}</td>
                  <td style={{ padding: '14px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>{e.description}</td>
                  <td style={{ padding: '14px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>{e.paidTo}</td>
                  <td style={{ padding: '14px', color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>{e.paymentFrom || '-'}</td>
                  <td style={{ padding: '14px', textAlign: 'right', color: '#ef4444', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(e.amount)}</td>
                  <td style={{ padding: '14px', textAlign: 'center' }}>
                    <button onClick={() => handleEditExpense(e)} style={{ background: 'transparent', border: '1px solid rgba(201, 162, 39, 0.3)', borderRadius: '4px', color: '#c9a227', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' }}>Edit</button>
                  </td>
                </tr>
              ))}
              {expenses.length > 0 && (
                <tr style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                  <td colSpan={7} style={{ padding: '14px', color: '#ef4444', fontWeight: '600' }}>Total Expenses</td>
                  <td style={{ padding: '14px', textAlign: 'right', color: '#ef4444', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0))}</td>
                </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============= REPORTS MODULE =============
  const ReportsModule = () => {
    const [reportData, setReportData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
      const loadReport = async () => {
        try {
          const data = await api.getIncomeExpenditure();
          setReportData(data);
        } catch (error) {
          console.error('Failed to load report:', error);
        } finally {
          setLoading(false);
        }
      };
      loadReport();
    }, []);

    if (loading || !reportData) {
      return <div className="page-container" style={{ color: '#fff' }}>Loading report...</div>;
    }

    const totalIncome = reportData.income.total;
    const totalExpenses = reportData.total_expenditure;
    const netSurplus = reportData.net_surplus;
    const expensesByCategory = reportData.expenditure || [];

    const downloadExpenseReport = () => {
      const wb = XLSX.utils.book_new();
      const wsData = [];

      // Title
      wsData.push(['HATIMI HILLS']);
      wsData.push(['KALIMI MOHALLA, PUNE']);
      wsData.push(['INCOME AND EXPENDITURE ACCOUNT - SHEHRULLAH 1447 H']);
      wsData.push([]);

      // Expenditure section
      wsData.push(['EXPENDITURE', '', '', 'INCOME', '']);
      wsData.push(['Category', 'Amount (₹)', '', 'Particulars', 'Amount (₹)']);

      const maxRows = Math.max(expensesByCategory.length + 2, 3);
      for (let i = 0; i < maxRows; i++) {
        const row = ['', '', '', '', ''];
        if (i < expensesByCategory.length) {
          const cat = expensesByCategory[i];
          row[0] = (cat.parent_id !== null ? '  ' : 'To ') + cat.category;
          row[1] = parseFloat(cat.amount || 0);
        } else if (i === expensesByCategory.length && netSurplus > 0) {
          row[0] = 'Surplus (Income over Expenditure)';
          row[1] = parseFloat(netSurplus);
        } else if (i === maxRows - 1) {
          row[0] = 'TOTAL';
          row[1] = parseFloat(totalIncome);
        }
        if (i === 0) {
          row[3] = 'By Voluntarily Contribution Received';
          row[4] = parseFloat(totalIncome);
        }
        if (i === maxRows - 1) {
          row[3] = 'TOTAL';
          row[4] = parseFloat(totalIncome);
        }
        wsData.push(row);
      }

      wsData.push([]);
      wsData.push(['SUMMARY']);
      wsData.push(['Total Income', parseFloat(totalIncome)]);
      wsData.push(['Total Expenses', parseFloat(totalExpenses)]);
      wsData.push([netSurplus >= 0 ? 'Net Surplus' : 'Net Deficit', parseFloat(Math.abs(netSurplus))]);

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [{ wch: 35 }, { wch: 15 }, { wch: 3 }, { wch: 35 }, { wch: 15 }];
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Income & Expenditure');
      XLSX.writeFile(wb, 'Income_Expenditure_Report_Shehrullah_1447H.xlsx');
    };

    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Financial Reports</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>Income & Expenditure Account - Shehrullah 1447 H</p>
          </div>
          <button
            onClick={downloadExpenseReport}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Download Excel
          </button>
        </div>

        {/* Income & Expenditure Statement */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
          borderRadius: '20px',
          padding: '32px',
          border: '1px solid rgba(201, 162, 39, 0.2)',
          marginBottom: '24px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px', paddingBottom: '24px', borderBottom: '2px solid rgba(201, 162, 39, 0.2)' }}>
            <h2 style={{ color: '#f4d03f', margin: '0 0 8px 0', fontSize: '24px' }}>HATIMI HILLS</h2>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 4px 0' }}>KALIMI MOHALLA, PUNE</p>
            <p style={{ color: '#c9a227', margin: 0, fontWeight: '600' }}>INCOME AND EXPENDITURE ACCOUNT - SHEHRULLAH 1447 H</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
            {/* Expenditure Side */}
            <div>
              <h3 style={{ color: '#ef4444', margin: '0 0 16px 0', fontSize: '16px', borderBottom: '1px solid rgba(239, 68, 68, 0.3)', paddingBottom: '8px' }}>EXPENDITURE</h3>
              <table style={{ width: '100%' }}>
                <tbody>
                  {expensesByCategory.map(cat => {
                    const isSubcategory = cat.parent_id !== null;
                    const amount = parseFloat(cat.amount || 0);
                    return (
                      <tr key={cat.id}>
                        <td style={{
                          padding: '8px 0',
                          paddingLeft: isSubcategory ? '20px' : '0',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '13px'
                        }}>
                          {isSubcategory ? '└─ ' : 'To '}{cat.category}
                        </td>
                        <td style={{ padding: '8px 0', textAlign: 'right', color: amount > 0 ? '#fff' : 'rgba(255, 255, 255, 0.3)', fontFamily: "'JetBrains Mono', monospace", fontSize: '13px' }}>{formatCurrency(amount)}</td>
                      </tr>
                    );
                  })}
                  {netSurplus > 0 && (
                    <tr style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <td style={{ padding: '12px 0', color: '#22c55e', fontWeight: '600' }}>Surplus (Income over Expenditure)</td>
                      <td style={{ padding: '12px 0', textAlign: 'right', color: '#22c55e', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(netSurplus)}</td>
                    </tr>
                  )}
                  <tr style={{ borderTop: '2px solid rgba(201, 162, 39, 0.3)' }}>
                    <td style={{ padding: '12px 0', color: '#f4d03f', fontWeight: '700' }}>TOTAL</td>
                    <td style={{ padding: '12px 0', textAlign: 'right', color: '#f4d03f', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalIncome)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Income Side */}
            <div>
              <h3 style={{ color: '#22c55e', margin: '0 0 16px 0', fontSize: '16px', borderBottom: '1px solid rgba(34, 197, 94, 0.3)', paddingBottom: '8px' }}>INCOME</h3>
              <table style={{ width: '100%' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '8px 0', color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>By Voluntarily Contribution Received</td>
                    <td style={{ padding: '8px 0', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontSize: '13px' }}>{formatCurrency(totalIncome)}</td>
                  </tr>
                  <tr style={{ borderTop: '2px solid rgba(201, 162, 39, 0.3)', marginTop: '200px' }}>
                    <td style={{ padding: '12px 0', color: '#f4d03f', fontWeight: '700' }}>TOTAL</td>
                    <td style={{ padding: '12px 0', textAlign: 'right', color: '#f4d03f', fontWeight: '700', fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalIncome)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(34, 197, 94, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>📥</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>Total Income</span>
            </div>
            <p style={{ color: '#22c55e', fontSize: '32px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalIncome)}</p>
          </div>

          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>📤</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>Total Expenses</span>
            </div>
            <p style={{ color: '#ef4444', fontSize: '32px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(totalExpenses)}</p>
          </div>

          <div style={{
            background: netSurplus >= 0 ? 'rgba(201, 162, 39, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            border: `1px solid ${netSurplus >= 0 ? 'rgba(201, 162, 39, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '24px' }}>{netSurplus >= 0 ? '✨' : '⚠️'}</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>{netSurplus >= 0 ? 'Net Surplus' : 'Net Deficit'}</span>
            </div>
            <p style={{ color: netSurplus >= 0 ? '#f4d03f' : '#ef4444', fontSize: '32px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>{formatCurrency(Math.abs(netSurplus))}</p>
          </div>
        </div>
      </div>
    );
  };

  // ============= CATEGORIES MODULE (Admin Only) =============
  const CategoriesModule = () => {
    const [categories, setCategories] = useState([]);
    const [flatCategories, setFlatCategories] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editCategory, setEditCategory] = useState(null);
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      parent_id: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    // Load categories from API
    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const data = await api.getCategories();
        setCategories(data.categories || []);
        setFlatCategories(data.flat || []);
        // Auto-expand all categories by default
        const allIds = new Set((data.flat || []).filter(c => c.subcategory_count > 0).map(c => c.id));
        setExpandedCategories(allIds);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setError('Failed to load categories: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    React.useEffect(() => {
      loadCategories();
    }, []);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');

      try {
        if (editCategory) {
          await api.updateCategory(editCategory.id, formData);
        } else {
          await api.createCategory(formData);
        }

        setShowForm(false);
        setEditCategory(null);
        setFormData({ name: '', description: '', parent_id: null });
        await loadCategories();
      } catch (error) {
        console.error(`Failed to ${editCategory ? 'update' : 'create'} category:`, error);
        setError(error.message || `Failed to ${editCategory ? 'update' : 'create'} category`);
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleDelete = async (categoryId) => {
      if (!window.confirm('Are you sure you want to delete this category?')) {
        return;
      }

      try {
        await api.deleteCategory(categoryId);
        await loadCategories();
      } catch (error) {
        console.error('Failed to delete category:', error);
        alert('Failed to delete category: ' + error.message);
      }
    };

    const toggleExpand = (categoryId) => {
      const newExpanded = new Set(expandedCategories);
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
      }
      setExpandedCategories(newExpanded);
    };

    const renderCategory = (category, level = 0) => {
      const hasSubcategories = category.subcategories && category.subcategories.length > 0;
      const isExpanded = expandedCategories.has(category.id);

      return (
        <React.Fragment key={category.id}>
          <tr style={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            background: level > 0 ? 'rgba(201, 162, 39, 0.02)' : 'transparent'
          }}>
            <td style={{ padding: '14px', paddingLeft: `${14 + level * 30}px`, color: '#fff', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {hasSubcategories && (
                  <button
                    onClick={() => toggleExpand(category.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#f4d03f',
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: '0',
                      width: '20px'
                    }}
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>
                )}
                {!hasSubcategories && <span style={{ width: '20px' }}></span>}
                <span style={{ fontWeight: level === 0 ? '600' : '400' }}>{category.name}</span>
                {level > 0 && <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '11px' }}>(sub)</span>}
              </div>
            </td>
            <td style={{ padding: '14px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
              {category.description || '-'}
            </td>
            <td style={{ padding: '14px', textAlign: 'center', color: '#fff', fontSize: '13px' }}>
              {category.subcategory_count || 0}
            </td>
            <td style={{ padding: '14px', textAlign: 'center', color: '#fff', fontSize: '13px' }}>
              {category.expense_count || 0}
            </td>
            <td style={{ padding: '14px', textAlign: 'center' }}>
              <span style={{
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '600',
                background: category.is_active ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                color: category.is_active ? '#22c55e' : '#ef4444',
                border: `1px solid ${category.is_active ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
              }}>
                {category.is_active ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </td>
            <td style={{ padding: '14px', textAlign: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    setEditCategory(category);
                    setFormData({
                      name: category.name,
                      description: category.description || '',
                      parent_id: category.parent_id
                    });
                    setShowForm(true);
                  }}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(59, 130, 246, 0.2)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '6px',
                    color: '#3b82f6',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >✏️ Edit</button>
                <button
                  onClick={() => {
                    setFormData({
                      name: '',
                      description: '',
                      parent_id: category.id
                    });
                    setShowForm(true);
                  }}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    color: '#22c55e',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >➕ Sub</button>
                <button
                  onClick={() => handleDelete(category.id)}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    color: '#ef4444',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >🗑️</button>
              </div>
            </td>
          </tr>
          {hasSubcategories && isExpanded && category.subcategories.map(sub => renderCategory(sub, level + 1))}
        </React.Fragment>
      );
    };

    return (
      <div className="page-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Expense Categories</h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.5)', margin: 0 }}>Manage categories and sub-categories for expense tracking</p>
          </div>
          <button
            onClick={() => {
              setEditCategory(null);
              setFormData({ name: '', description: '', parent_id: null });
              setShowForm(true);
            }}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
              border: 'none',
              borderRadius: '10px',
              color: '#0a1628',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >+ Add Category</button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '24px',
            color: '#fca5a5',
            fontSize: '13px'
          }}>{error}</div>
        )}

        {showForm && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div
              className="form-modal"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                borderRadius: '20px',
                padding: '32px',
                width: '500px',
                border: '1px solid rgba(201, 162, 39, 0.2)'
            }}>
              <h3 style={{ color: '#f4d03f', margin: '0 0 24px 0' }}>
                {editCategory ? 'Edit Category' : formData.parent_id ? 'Add Sub-Category' : 'Add Category'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Enter category name"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    placeholder="Optional description"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {!editCategory && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>
                      Parent Category
                    </label>
                    <select
                      value={formData.parent_id || ''}
                      onChange={(e) => setFormData({ ...formData, parent_id: e.target.value ? parseInt(e.target.value) : null })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option style={{ background: '#1e293b', color: '#fff' }} value="">None (Top-level category)</option>
                      {flatCategories.filter(c => c.parent_id === null).map(cat => (
                        <option style={{ background: '#1e293b', color: '#fff' }} key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    {formData.parent_id && (
                      <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '11px', marginTop: '6px', marginBottom: 0 }}>
                        This will be created as a sub-category
                      </p>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditCategory(null);
                      setFormData({ name: '', description: '', parent_id: null });
                      setError('');
                    }}
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: '#fff',
                      cursor: 'pointer',
                      opacity: isSubmitting ? 0.5 : 1
                    }}
                  >Cancel</button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#0a1628',
                      fontWeight: '600',
                      cursor: 'pointer',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  >
                    {isSubmitting ? (editCategory ? 'Updating...' : 'Creating...') : (editCategory ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.5)' }}>
            Loading categories...
          </div>
        ) : (
          <div style={{
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(201, 162, 39, 0.1)' }}>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Category Name</th>
                  <th style={{ padding: '14px', textAlign: 'left', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Description</th>
                  <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Sub-Categories</th>
                  <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Expenses</th>
                  <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Status</th>
                  <th style={{ padding: '14px', textAlign: 'center', color: '#f4d03f', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
                      No categories found. Click "Add Category" to create one.
                    </td>
                  </tr>
                ) : (
                  categories.map(cat => renderCategory(cat))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============= USERS MODULE =============
  const UsersModule = () => {
    const [users, setUsers] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editUserData, setEditUserData] = useState({
      username: '',
      name: '',
      role: 'user',
      password: '',
      is_active: true
    });
    const [newUser, setNewUser] = useState({
      username: '',
      password: '',
      name: '',
      role: 'user'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Load users from API
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const usersData = await api.getUsers();
        setUsers(usersData);
      } catch (error) {
        console.error('Failed to load users:', error);
        setError('Failed to load users: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    React.useEffect(() => {
      loadUsers();
    }, []);

    const handleCreateUser = async () => {
      if (!newUser.username || !newUser.password || !newUser.name) {
        setError('Please fill in all required fields');
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        await api.createUser(newUser);
        setShowCreateForm(false);
        setNewUser({ username: '', password: '', name: '', role: 'user' });
        await loadUsers();
      } catch (error) {
        console.error('Failed to create user:', error);
        setError('Failed to create user: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    const handleOpenEditUser = (user) => {
      setEditingUser(user);
      setEditUserData({
        username: user.username,
        name: user.name,
        role: user.role,
        password: '',
        is_active: user.is_active
      });
      setError('');
    };

    const handleUpdateUser = async () => {
      if (!editUserData.username || !editUserData.name || !editUserData.role) {
        setError('Username, name, and role are required');
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        const updateData = {
          username: editUserData.username,
          name: editUserData.name,
          role: editUserData.role,
          is_active: editUserData.is_active
        };
        if (editUserData.password && editUserData.password.trim() !== '') {
          updateData.password = editUserData.password;
        }
        await api.updateUser(editingUser.id, updateData);
        setEditingUser(null);
        await loadUsers();
      } catch (error) {
        console.error('Failed to update user:', error);
        setError('Failed to update user: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    const handleToggleUser = async (user) => {
      setIsLoading(true);
      setError('');
      try {
        await api.updateUser(user.id, {
          username: user.username,
          name: user.name,
          role: user.role,
          is_active: !user.is_active
        });
        await loadUsers();
      } catch (error) {
        console.error('Failed to toggle user:', error);
        setError('Failed to update user status: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    const handleDeleteUser = async (user) => {
      if (!window.confirm(`Are you sure you want to delete user "${user.username}"? This will deactivate the account.`)) {
        return;
      }
      setIsLoading(true);
      setError('');
      try {
        await api.deleteUser(user.id);
        await loadUsers();
      } catch (error) {
        console.error('Failed to delete user:', error);
        setError('Failed to delete user: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <div className="page-container" style={{ maxWidth: '1200px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <h1 style={{ color: '#f4d03f', fontSize: '28px', fontWeight: '600', margin: 0 }}>
            User Management
          </h1>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)',
              color: '#0a1628',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            + Add User
          </button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#fca5a5',
            fontSize: '13px'
          }}>{error}</div>
        )}

        {showCreateForm && (
          <div style={{
            background: 'rgba(15, 23, 42, 0.9)',
            padding: '24px',
            borderRadius: '12px',
            marginBottom: '24px',
            border: '1px solid rgba(201, 162, 39, 0.2)'
          }}>
            <h3 style={{ color: '#f4d03f', margin: '0 0 20px 0' }}>Create New User</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="Username *"
                value={newUser.username}
                onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                disabled={isLoading}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#fff'
                }}
              />
              <input
                type="text"
                placeholder="Full Name *"
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                disabled={isLoading}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#fff'
                }}
              />
              <input
                type="password"
                placeholder="Password *"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                disabled={isLoading}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#fff'
                }}
              />
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                disabled={isLoading}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  padding: '10px',
                  color: '#fff'
                }}
              >
                <option style={{ background: '#1e293b', color: '#fff' }} value="user">User</option>
                <option style={{ background: '#1e293b', color: '#fff' }} value="admin">Admin</option>
                <option style={{ background: '#1e293b', color: '#fff' }} value="inventory_manager">Inventory Manager</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleCreateUser}
                disabled={isLoading}
                style={{
                  background: '#c9a227',
                  color: '#0a1628',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? 'Creating...' : 'Create User'}
              </button>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setError('');
                }}
                disabled={isLoading}
                style={{
                  background: 'transparent',
                  color: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              borderRadius: '20px',
              padding: '32px',
              width: '480px',
              border: '1px solid rgba(201, 162, 39, 0.2)'
            }}>
              <h3 style={{ color: '#f4d03f', margin: '0 0 24px 0' }}>Edit User - {editingUser.username}</h3>
              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#fca5a5',
                  fontSize: '13px'
                }}>{error}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Username</label>
                  <input
                    type="text"
                    value={editUserData.username}
                    onChange={(e) => setEditUserData({...editUserData, username: e.target.value})}
                    disabled={isLoading}
                    style={{ width: '100%', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '12px', color: '#fff', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Full Name</label>
                  <input
                    type="text"
                    value={editUserData.name}
                    onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                    disabled={isLoading}
                    style={{ width: '100%', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '12px', color: '#fff', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>Role</label>
                  <select
                    value={editUserData.role}
                    onChange={(e) => setEditUserData({...editUserData, role: e.target.value})}
                    disabled={isLoading}
                    style={{ width: '100%', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '12px', color: '#fff', boxSizing: 'border-box' }}
                  >
                    <option style={{ background: '#1e293b', color: '#fff' }} value="user">User</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="admin">Admin</option>
                    <option style={{ background: '#1e293b', color: '#fff' }} value="inventory_manager">Inventory Manager</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', marginBottom: '6px', textTransform: 'uppercase' }}>New Password (leave blank to keep current)</label>
                  <input
                    type="password"
                    value={editUserData.password}
                    onChange={(e) => setEditUserData({...editUserData, password: e.target.value})}
                    disabled={isLoading}
                    placeholder="Enter new password..."
                    style={{ width: '100%', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '12px', color: '#fff', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', textTransform: 'uppercase' }}>Active Status</label>
                  <button
                    type="button"
                    onClick={() => setEditUserData({...editUserData, is_active: !editUserData.is_active})}
                    disabled={isLoading}
                    style={{
                      background: editUserData.is_active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: editUserData.is_active ? '#22c55e' : '#ef4444',
                      border: `1px solid ${editUserData.is_active ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                      padding: '6px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {editUserData.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => { setEditingUser(null); setError(''); }}
                  disabled={isLoading}
                  style={{ flex: 1, padding: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: '#fff', cursor: 'pointer', opacity: isLoading ? 0.5 : 1 }}
                >Cancel</button>
                <button
                  onClick={handleUpdateUser}
                  disabled={isLoading}
                  style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)', border: 'none', borderRadius: '8px', color: '#0a1628', fontWeight: '600', cursor: 'pointer', opacity: isLoading ? 0.7 : 1 }}
                >{isLoading ? 'Updating...' : 'Update User'}</button>
              </div>
            </div>
          </div>
        )}

        <div style={{
          background: 'rgba(15, 23, 42, 0.6)',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(201, 162, 39, 0.2)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(201, 162, 39, 0.1)' }}>
                <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Username</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Name</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Role</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Created</th>
                <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id} style={{ borderBottom: index < users.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none' }}>
                  <td style={{ padding: '16px', color: '#fff' }}>{user.username}</td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>{user.name}</td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      background: user.role === 'admin' ? 'rgba(239, 68, 68, 0.2)' : user.role === 'inventory_manager' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                      color: user.role === 'admin' ? '#ef4444' : user.role === 'inventory_manager' ? '#f59e0b' : '#3b82f6',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      textTransform: 'uppercase'
                    }}>
                      {user.role === 'inventory_manager' ? 'Inv. Manager' : user.role}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{
                      background: user.is_active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: user.is_active ? '#22c55e' : '#ef4444',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <button
                      style={{
                        background: 'transparent',
                        border: '1px solid rgba(201, 162, 39, 0.3)',
                        borderRadius: '4px',
                        color: '#c9a227',
                        padding: '6px 12px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        marginRight: '8px'
                      }}
                      onClick={() => handleOpenEditUser(user)}
                    >
                      Edit
                    </button>
                    {user.username !== 'admin' && (
                      <>
                        <button
                          onClick={() => handleToggleUser(user)}
                          disabled={isLoading}
                          style={{
                            background: 'transparent',
                            border: `1px solid ${user.is_active ? 'rgba(239, 68, 68, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                            borderRadius: '4px',
                            color: user.is_active ? '#ef4444' : '#22c55e',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          {user.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          disabled={isLoading}
                          style={{
                            background: 'transparent',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '4px',
                            color: '#ef4444',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============= INVENTORY DASHBOARD MODULE =============
  const InventoryDashboardModule = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      loadDashboard();
    }, []);

    const loadDashboard = async () => {
      try {
        setLoading(true);
        const response = await api.get('/inventory-reports/dashboard');
        setDashboardData(response.data);
      } catch (error) {
        console.error('Error loading inventory dashboard:', error);
        alert('Failed to load inventory dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (loading) {
      return (
        <div className="responsive-container" style={{ padding: '32px', textAlign: 'center' }}>
          <p style={{ color: '#fff' }}>Loading...</p>
        </div>
      );
    }

    if (!dashboardData) {
      return (
        <div className="responsive-container" style={{ padding: '32px', textAlign: 'center' }}>
          <p style={{ color: '#fff' }}>No data available</p>
        </div>
      );
    }

    const StatCard = ({ icon, title, value, subtitle, color }) => (
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid rgba(255, 255, 255, 0.05)'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          background: `${color}15`,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          marginBottom: '16px'
        }}>{icon}</div>
        <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>{title}</p>
        <h3 style={{ margin: '8px 0 4px 0', color: color, fontSize: '28px', fontWeight: '700' }}>{value}</h3>
        {subtitle && <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.4)', fontSize: '12px' }}>{subtitle}</p>}
      </div>
    );

    return (
      <div className="responsive-container" style={{ padding: '32px' }}>
        <h1 style={{ color: '#f4d03f', marginBottom: '24px' }}>📦 Inventory Dashboard</h1>

        {/* Overview Stats */}
        <div className="responsive-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <StatCard
            icon="📦"
            title="Total Items"
            value={dashboardData.inventory.total_items}
            subtitle="Active inventory items"
            color="#3b82f6"
          />
          <StatCard
            icon="⚠️"
            title="Low Stock"
            value={dashboardData.inventory.low_stock_items}
            subtitle="Items below minimum"
            color="#f59e0b"
          />
          <StatCard
            icon="🚫"
            title="Out of Stock"
            value={dashboardData.inventory.out_of_stock_items}
            subtitle="Items with zero stock"
            color="#ef4444"
          />
          <StatCard
            icon="💰"
            title="Total Value"
            value={formatCurrency(parseFloat(dashboardData.inventory.total_value))}
            subtitle="Current inventory value"
            color="#10b981"
          />
        </div>

        {/* Yadi & PO Stats */}
        <div className="responsive-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}>
            <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>📋 Daily Yadis (This Month)</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Total Yadis:</span>
              <span style={{ color: '#fff', fontWeight: '600' }}>{dashboardData.yadis.this_month_count}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Total Expense:</span>
              <span style={{ color: '#fff', fontWeight: '600' }}>{formatCurrency(parseFloat(dashboardData.yadis.this_month_expense))}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Avg Cost/Thaal:</span>
              <span style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(parseFloat(dashboardData.yadis.avg_cost_per_thaal))}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Pending:</span>
              <span style={{ color: '#f59e0b', fontWeight: '600' }}>{dashboardData.yadis.pending_count}</span>
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.05)'
          }}>
            <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>🛒 Purchase Orders (This Month)</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Total POs:</span>
              <span style={{ color: '#fff', fontWeight: '600' }}>{dashboardData.purchase_orders.this_month_count}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Total Value:</span>
              <span style={{ color: '#fff', fontWeight: '600' }}>{formatCurrency(parseFloat(dashboardData.purchase_orders.this_month_value))}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Pending:</span>
              <span style={{ color: '#f59e0b', fontWeight: '600' }}>{dashboardData.purchase_orders.pending_count}</span>
            </div>
          </div>
        </div>

        {/* Recent Alerts */}
        {dashboardData.recent_alerts && dashboardData.recent_alerts.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            marginBottom: '16px'
          }}>
            <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>🚨 Stock Alerts</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' }}>Item</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' }}>Category</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' }}>Alert Type</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' }}>Current Stock</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' }}>Min Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.recent_alerts.slice(0, 10).map((alert, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <td style={{ padding: '12px', color: '#fff' }}>{alert.item_name}</td>
                      <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>{alert.category}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          background: alert.alert_type === 'Out of Stock' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                          color: alert.alert_type === 'Out of Stock' ? '#ef4444' : '#f59e0b',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {alert.alert_type}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#fff' }}>{alert.current_stock} {alert.unit}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{alert.minimum_stock} {alert.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <button
            onClick={() => setActiveModule('inventory-items')}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            📦 Manage Items
          </button>
          <button
            onClick={() => setActiveModule('daily-yadi')}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            📋 Create Yadi
          </button>
          <button
            onClick={() => setActiveModule('purchase-orders')}
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            🛒 Create PO
          </button>
          <button
            onClick={() => setActiveModule('inventory-reports')}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            📊 View Reports
          </button>
        </div>
      </div>
    );
  };

  // ============= INVENTORY ITEMS MODULE =============
  const InventoryItemsModule = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ category: '', low_stock: false });
    const [showForm, setShowForm] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
      item_name: '',
      category: 'Kirana',
      unit: 'kg',
      current_price: '',
      current_stock: '',
      minimum_stock: '',
      reorder_quantity: ''
    });

    useEffect(() => {
      loadItems();
    }, [filter]);

    const loadItems = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (filter.category) params.append('category', filter.category);
        if (filter.low_stock) params.append('low_stock', 'true');
        const response = await api.get(`/inventory?${params.toString()}`);
        setItems(response.data);
      } catch (error) {
        console.error('Error loading items:', error);
        alert('Failed to load inventory items');
      } finally {
        setLoading(false);
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (editingItem) {
          await api.put(`/inventory/${editingItem.id}`, formData);
        } else {
          await api.post('/inventory', formData);
        }
        setShowForm(false);
        setEditingItem(null);
        setFormData({
          item_name: '',
          category: 'Kirana',
          unit: 'kg',
          current_price: '',
          current_stock: '',
          minimum_stock: '',
          reorder_quantity: ''
        });
        loadItems();
      } catch (error) {
        console.error('Error saving item:', error);
        alert(error.message || 'Failed to save item');
      }
    };

    const handleEdit = (item) => {
      setEditingItem(item);
      setFormData({
        item_name: item.item_name,
        category: item.category,
        unit: item.unit,
        current_price: item.current_price,
        current_stock: item.current_stock,
        minimum_stock: item.minimum_stock,
        reorder_quantity: item.reorder_quantity
      });
      setShowForm(true);
    };

    const handleDelete = async (id) => {
      if (!confirm('Are you sure you want to delete this item?')) return;
      try {
        await api.delete(`/inventory/${id}`);
        loadItems();
      } catch (error) {
        console.error('Error deleting item:', error);
        alert(error.message || 'Failed to delete item');
      }
    };

    return (
      <div className="responsive-container" style={{ padding: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <h1 style={{ color: '#f4d03f', margin: 0 }}>🏪 Inventory Items</h1>
          {currentUser.role === 'admin' && (
            <button
              onClick={() => { setShowForm(true); setEditingItem(null); }}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              + Add Item
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <select
            value={filter.category}
            onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '10px 16px',
              color: '#fff',
              fontSize: '14px'
            }}
          >
            <option value="">All Categories</option>
            <option value="Kirana">Kirana</option>
            <option value="Vegetable">Vegetable</option>
            <option value="Dairy">Dairy</option>
            <option value="Non-Veg">Non-Veg</option>
            <option value="Other">Other</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={filter.low_stock}
              onChange={(e) => setFilter({ ...filter, low_stock: e.target.checked })}
            />
            Show Low Stock Only
          </label>
        </div>

        {/* Items Table */}
        {loading ? (
          <p style={{ color: '#fff' }}>Loading...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Item Name</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Category</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Current Stock</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Min Stock</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Price</th>
                  <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={{ padding: '16px', color: '#fff' }}>{item.item_name}</td>
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)' }}>{item.category}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: item.current_stock <= item.minimum_stock ? '#ef4444' : '#10b981' }}>
                      {item.current_stock} {item.unit}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{item.minimum_stock} {item.unit}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#fff' }}>{formatCurrency(item.current_price)}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {currentUser.role === 'admin' && (
                        <>
                          <button
                            onClick={() => handleEdit(item)}
                            style={{
                              background: 'rgba(59, 130, 246, 0.1)',
                              border: '1px solid rgba(59, 130, 246, 0.2)',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              color: '#3b82f6',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginRight: '8px'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#1e293b',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: '#f4d03f', marginBottom: '24px' }}>{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Item Name *</label>
                  <input
                    type="text"
                    value={formData.item_name}
                    onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    >
                      <option value="Kirana">Kirana</option>
                      <option value="Vegetable">Vegetable</option>
                      <option value="Dairy">Dairy</option>
                      <option value="Non-Veg">Non-Veg</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Unit *</label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    >
                      <option value="kg">kg</option>
                      <option value="ltr">ltr</option>
                      <option value="pcs">pcs</option>
                      <option value="gaddi">gaddi</option>
                      <option value="packet">packet</option>
                      <option value="dozen">dozen</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Price *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.current_price}
                      onChange={(e) => setFormData({ ...formData, current_price: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Current Stock</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.current_stock}
                      onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Min Stock</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.minimum_stock}
                      onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Reorder Qty</label>
                    <input
                      type="number"
                      step="0.001"
                      value={formData.reorder_quantity}
                      onChange={(e) => setFormData({ ...formData, reorder_quantity: e.target.value })}
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {editingItem ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingItem(null); }}
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#fff',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============= DAILY YADI MODULE =============
  const DailyYadiModule = () => {
    const [yadis, setYadis] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingYadiId, setEditingYadiId] = useState(null);
    const [viewYadi, setViewYadi] = useState(null);
    const [yadiItems, setYadiItems] = useState([]);
    const [formData, setFormData] = useState({
      yadi_no: '',
      yadi_date: new Date().toISOString().split('T')[0],
      menu_description: '',
      total_thaals: '',
      remarks: '',
      items: []
    });

    useEffect(() => {
      loadYadis();
      loadInventoryItems();
      loadNextNumber();
    }, []);

    const loadYadis = async () => {
      try {
        setLoading(true);
        const response = await api.get('/yadi');
        setYadis(response.data);
      } catch (error) {
        console.error('Error loading yadis:', error);
        alert('Failed to load yadis');
      } finally {
        setLoading(false);
      }
    };

    const loadInventoryItems = async () => {
      try {
        const response = await api.get('/inventory?is_active=true');
        setInventoryItems(response.data);
      } catch (error) {
        console.error('Error loading inventory items:', error);
      }
    };

    const loadNextNumber = async () => {
      try {
        const response = await api.get('/yadi/next-number');
        setFormData(prev => ({ ...prev, yadi_no: response.next_yadi_no }));
      } catch (error) {
        console.error('Error loading next number:', error);
      }
    };

    const addItemToYadi = () => {
      setYadiItems([...yadiItems, { item_id: '', quantity: '', unit_price: '', item_name: '', unit: '' }]);
    };

    const updateYadiItem = (index, field, value) => {
      const updated = [...yadiItems];
      updated[index][field] = value;
      if (field === 'item_id') {
        const selectedItem = inventoryItems.find(i => i.id === parseInt(value));
        if (selectedItem) {
          updated[index].item_name = selectedItem.item_name;
          updated[index].unit = selectedItem.unit;
          updated[index].unit_price = selectedItem.current_price;
        }
      }
      setYadiItems(updated);
    };

    const removeYadiItem = (index) => {
      setYadiItems(yadiItems.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const yadiData = {
          ...formData,
          items: yadiItems.map(item => ({
            item_id: parseInt(item.item_id),
            quantity: parseFloat(item.quantity),
            unit_price: parseFloat(item.unit_price)
          }))
        };
        if (editingYadiId) {
          await api.put(`/yadi/${editingYadiId}`, yadiData);
        } else {
          await api.post('/yadi', yadiData);
        }
        setShowForm(false);
        setEditingYadiId(null);
        setYadiItems([]);
        loadYadis();
        loadNextNumber();
      } catch (error) {
        console.error('Error saving yadi:', error);
        alert(error.message || 'Failed to save yadi');
      }
    };

    const handleStatusChange = async (id, action) => {
      try {
        if (action === 'submit') {
          await api.put(`/yadi/${id}/submit`);
        } else if (action === 'approve') {
          if (!confirm('Are you sure you want to approve this yadi? Stock will be deducted.')) return;
          await api.put(`/yadi/${id}/approve`);
        } else if (action === 'reject') {
          const remarks = prompt('Rejection reason:');
          if (!remarks) return;
          await api.put(`/yadi/${id}/reject`, { remarks });
        }
        loadYadis();
      } catch (error) {
        console.error(`Error ${action}ing yadi:`, error);
        alert(error.message || `Failed to ${action} yadi`);
      }
    };

    const viewYadiDetails = async (yadi) => {
      try {
        const response = await api.get(`/yadi/${yadi.id}`);
        setViewYadi(response.data);
      } catch (error) {
        console.error('Error loading yadi details:', error);
        alert('Failed to load yadi details');
      }
    };

    const downloadYadiExcel = async (yadi) => {
      try {
        let yadiData = viewYadi;
        if (!yadiData || yadiData.yadi.id !== yadi.id) {
          const response = await api.get(`/yadi/${yadi.id}`);
          yadiData = response.data;
        }

        const wb = XLSX.utils.book_new();
        const wsData = [];

        // Title
        wsData.push(['HATIMI HILLS MARKAZ - Daily Yadi']);
        wsData.push([]);

        // Yadi details
        wsData.push(['Yadi Number', yadiData.yadi.yadi_no, '', 'Date', formatDate(yadiData.yadi.yadi_date)]);
        wsData.push(['Total Thaals', yadiData.yadi.total_thaals, '', 'Status', yadiData.yadi.status]);
        wsData.push(['Menu Description', yadiData.yadi.menu_description || 'N/A']);
        wsData.push([]);

        // Items header
        wsData.push(['Sr.', 'Item', 'Qty', 'Unit', 'Price (₹)', 'Amount (₹)']);

        // Items
        yadiData.items.forEach((item, idx) => {
          wsData.push([
            idx + 1,
            item.item_name,
            parseFloat(item.quantity),
            item.unit,
            parseFloat(item.unit_price),
            parseFloat(item.total_amount)
          ]);
        });

        // Totals
        wsData.push([]);
        wsData.push(['', '', '', '', 'Total Expense:', parseFloat(yadiData.yadi.total_expense)]);
        wsData.push(['', '', '', '', 'Cost per Thaal:', parseFloat(yadiData.yadi.cost_per_thaal)]);

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Column widths
        ws['!cols'] = [
          { wch: 5 },   // Sr.
          { wch: 30 },  // Item
          { wch: 10 },  // Qty
          { wch: 8 },   // Unit
          { wch: 12 },  // Price
          { wch: 14 },  // Amount
        ];

        // Merge title row
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];

        XLSX.utils.book_append_sheet(wb, ws, 'Yadi');
        XLSX.writeFile(wb, `Yadi_${yadiData.yadi.yadi_no.replace('/', '-')}_${new Date(yadiData.yadi.yadi_date).toISOString().split('T')[0]}.xlsx`);
      } catch (error) {
        console.error('Error downloading yadi:', error);
        alert(error.message || 'Failed to download yadi');
      }
    };

    const editYadiDetails = async (yadi) => {
      try {
        const response = await api.get(`/yadi/${yadi.id}`);
        const yadiData = response.data;
        setEditingYadiId(yadi.id);
        setFormData({
          yadi_no: yadiData.yadi.yadi_no,
          yadi_date: yadiData.yadi.yadi_date ? new Date(yadiData.yadi.yadi_date).toISOString().split('T')[0] : '',
          menu_description: yadiData.yadi.menu_description || '',
          total_thaals: yadiData.yadi.total_thaals || '',
          remarks: yadiData.yadi.remarks || '',
          items: []
        });
        setYadiItems(yadiData.items.map(item => ({
          item_id: String(item.item_id),
          quantity: String(item.quantity),
          unit_price: String(item.unit_price),
          item_name: item.item_name,
          unit: item.unit || ''
        })));
        setShowForm(true);
      } catch (error) {
        console.error('Error loading yadi for edit:', error);
        alert('Failed to load yadi for editing');
      }
    };

    const calculateTotal = () => {
      return yadiItems.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0));
      }, 0);
    };

    return (
      <div className="responsive-container" style={{ padding: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <h1 style={{ color: '#f4d03f', margin: 0 }}>📋 Daily Yadi</h1>
          <button
            onClick={() => setShowForm(true)}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            + Create Yadi
          </button>
        </div>

        {/* Yadis Table */}
        {loading ? (
          <p style={{ color: '#fff' }}>Loading...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Yadi No</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Thaals</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Expense</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Cost/Thaal</th>
                  <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {yadis.map((yadi) => (
                  <tr key={yadi.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={{ padding: '16px', color: '#fff' }}>{yadi.yadi_no}</td>
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)' }}>{formatDate(yadi.yadi_date)}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#fff' }}>{yadi.total_thaals}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#fff' }}>{formatCurrency(yadi.total_expense)}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#10b981' }}>{formatCurrency(yadi.cost_per_thaal)}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{
                        background: yadi.status === 'Approved' ? 'rgba(16, 185, 129, 0.1)' :
                                   yadi.status === 'Rejected' ? 'rgba(239, 68, 68, 0.1)' :
                                   yadi.status === 'Submitted' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                        color: yadi.status === 'Approved' ? '#10b981' :
                               yadi.status === 'Rejected' ? '#ef4444' :
                               yadi.status === 'Submitted' ? '#f59e0b' : '#9ca3af',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {yadi.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <button
                        onClick={() => viewYadiDetails(yadi)}
                        style={{
                          background: 'rgba(59, 130, 246, 0.1)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          color: '#3b82f6',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginRight: '4px'
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => downloadYadiExcel(yadi)}
                        style={{
                          background: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.2)',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          color: '#10b981',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginRight: '4px'
                        }}
                      >
                        Download
                      </button>
                      {yadi.status === 'Draft' && (
                        <button
                          onClick={() => editYadiDetails(yadi)}
                          style={{
                            background: 'rgba(201, 162, 39, 0.1)',
                            border: '1px solid rgba(201, 162, 39, 0.2)',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            color: '#c9a227',
                            cursor: 'pointer',
                            fontSize: '12px',
                            marginRight: '4px'
                          }}
                        >
                          Edit
                        </button>
                      )}
                      {yadi.status === 'Draft' && (
                        <button
                          onClick={() => handleStatusChange(yadi.id, 'submit')}
                          style={{
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.2)',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            color: '#f59e0b',
                            cursor: 'pointer',
                            fontSize: '12px',
                            marginRight: '4px'
                          }}
                        >
                          Submit
                        </button>
                      )}
                      {yadi.status === 'Submitted' && currentUser.role === 'admin' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(yadi.id, 'approve')}
                            style={{
                              background: 'rgba(16, 185, 129, 0.1)',
                              border: '1px solid rgba(16, 185, 129, 0.2)',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              color: '#10b981',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginRight: '4px'
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusChange(yadi.id, 'reject')}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Yadi Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              background: '#1e293b',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: '#f4d03f', marginBottom: '24px' }}>{editingYadiId ? 'Edit Daily Yadi' : 'Create Daily Yadi'}</h2>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Yadi Number</label>
                    <input
                      type="text"
                      value={formData.yadi_no}
                      readOnly
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Date *</label>
                    <input
                      type="date"
                      value={formData.yadi_date}
                      onChange={(e) => setFormData({ ...formData, yadi_date: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Total Thaals *</label>
                    <input
                      type="number"
                      value={formData.total_thaals}
                      onChange={(e) => setFormData({ ...formData, total_thaals: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Menu Description</label>
                  <textarea
                    value={formData.menu_description}
                    onChange={(e) => setFormData({ ...formData, menu_description: e.target.value })}
                    rows="2"
                    style={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ color: '#f4d03f', margin: 0 }}>Items</h3>
                    <button
                      type="button"
                      onClick={addItemToYadi}
                      style={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        color: '#10b981',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      + Add Item
                    </button>
                  </div>
                  {yadiItems.map((item, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '12px', marginBottom: '12px', alignItems: 'end' }}>
                      <select
                        value={item.item_id}
                        onChange={(e) => updateYadiItem(index, 'item_id', e.target.value)}
                        required
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          color: '#fff',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select Item</option>
                        {inventoryItems.map(invItem => (
                          <option key={invItem.id} value={invItem.id}>
                            {invItem.item_name} ({invItem.current_stock} {invItem.unit})
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        step="0.001"
                        placeholder="Quantity"
                        value={item.quantity}
                        onChange={(e) => updateYadiItem(index, 'quantity', e.target.value)}
                        required
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          color: '#fff',
                          fontSize: '14px'
                        }}
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateYadiItem(index, 'unit_price', e.target.value)}
                        required
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          color: '#fff',
                          fontSize: '14px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeYadiItem(index)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: '6px',
                          padding: '12px',
                          color: '#ef4444',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>Total Expense:</span>
                    <span style={{ color: '#10b981', fontSize: '20px', fontWeight: '700' }}>{formatCurrency(calculateTotal())}</span>
                  </div>
                  {formData.total_thaals && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                      <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>Cost per Thaal:</span>
                      <span style={{ color: '#f4d03f', fontSize: '16px', fontWeight: '600' }}>
                        {formatCurrency(calculateTotal() / parseInt(formData.total_thaals || 1))}
                      </span>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    {editingYadiId ? 'Update Yadi' : 'Create Yadi'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingYadiId(null); setYadiItems([]); }}
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#fff',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '14px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Yadi Details Modal */}
        {viewYadi && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              background: '#1e293b',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '800px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: '#f4d03f', marginBottom: '24px' }}>Yadi Details</h2>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 4px 0' }}>Yadi Number</p>
                    <p style={{ color: '#fff', fontSize: '16px', margin: 0 }}>{viewYadi.yadi.yadi_no}</p>
                  </div>
                  <div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 4px 0' }}>Date</p>
                    <p style={{ color: '#fff', fontSize: '16px', margin: 0 }}>{formatDate(viewYadi.yadi.yadi_date)}</p>
                  </div>
                  <div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 4px 0' }}>Total Thaals</p>
                    <p style={{ color: '#fff', fontSize: '16px', margin: 0 }}>{viewYadi.yadi.total_thaals}</p>
                  </div>
                  <div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 4px 0' }}>Status</p>
                    <p style={{ color: '#fff', fontSize: '16px', margin: 0 }}>{viewYadi.yadi.status}</p>
                  </div>
                </div>
                <div>
                  <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Menu Description</p>
                  <p style={{ color: '#fff', fontSize: '14px', margin: 0 }}>{viewYadi.yadi.menu_description || 'N/A'}</p>
                </div>
              </div>

              <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>Items</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Item</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Qty</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Price</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {viewYadi.items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <td style={{ padding: '12px', color: '#fff' }}>{item.item_name}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{item.quantity} {item.unit}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{formatCurrency(item.unit_price)}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#fff' }}>{formatCurrency(item.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid rgba(255, 255, 255, 0.1)' }}>
                    <td colSpan="3" style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '600' }}>Total Expense:</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#10b981', fontSize: '18px', fontWeight: '700' }}>{formatCurrency(viewYadi.yadi.total_expense)}</td>
                  </tr>
                  <tr>
                    <td colSpan="3" style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '600' }}>Cost per Thaal:</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '16px', fontWeight: '700' }}>{formatCurrency(viewYadi.yadi.cost_per_thaal)}</td>
                  </tr>
                </tfoot>
              </table>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => downloadYadiExcel(viewYadi.yadi)}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Download Excel
                </button>
                <button
                  onClick={() => setViewYadi(null)}
                  style={{
                    flex: 1,
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: '#fff',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  // ============= PURCHASE ORDERS MODULE =============
  const PurchaseOrdersModule = () => {
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showReceiveForm, setShowReceiveForm] = useState(false);
    const [selectedPO, setSelectedPO] = useState(null);
    const [poItems, setPOItems] = useState([]);
    const [formData, setFormData] = useState({
      po_number: '',
      po_date: new Date().toISOString().split('T')[0],
      vendor_name: '',
      vendor_contact: '',
      delivery_date: '',
      remarks: '',
      items: []
    });

    useEffect(() => {
      loadPurchaseOrders();
      loadInventoryItems();
      loadNextNumber();
    }, []);

    const loadPurchaseOrders = async () => {
      try {
        setLoading(true);
        const response = await api.get('/purchase-orders');
        setPurchaseOrders(response.data);
      } catch (error) {
        console.error('Error loading purchase orders:', error);
        alert('Failed to load purchase orders');
      } finally {
        setLoading(false);
      }
    };

    const loadInventoryItems = async () => {
      try {
        const response = await api.get('/inventory?is_active=true');
        setInventoryItems(response.data);
      } catch (error) {
        console.error('Error loading inventory items:', error);
      }
    };

    const loadNextNumber = async () => {
      try {
        const response = await api.get('/purchase-orders/next-number');
        setFormData(prev => ({ ...prev, po_number: response.next_po_number }));
      } catch (error) {
        console.error('Error loading next number:', error);
      }
    };

    const addItemToPO = () => {
      setPOItems([...poItems, { item_id: '', quantity_ordered: '', unit_price: '', item_name: '', unit: '' }]);
    };

    const updatePOItem = (index, field, value) => {
      const updated = [...poItems];
      updated[index][field] = value;
      if (field === 'item_id') {
        const selectedItem = inventoryItems.find(i => i.id === parseInt(value));
        if (selectedItem) {
          updated[index].item_name = selectedItem.item_name;
          updated[index].unit = selectedItem.unit;
          updated[index].unit_price = selectedItem.current_price;
        }
      }
      setPOItems(updated);
    };

    const removePOItem = (index) => {
      setPOItems(poItems.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const poData = {
          ...formData,
          items: poItems.map(item => ({
            item_id: parseInt(item.item_id),
            quantity_ordered: parseFloat(item.quantity_ordered),
            unit_price: parseFloat(item.unit_price)
          }))
        };
        await api.post('/purchase-orders', poData);
        setShowForm(false);
        setPOItems([]);
        loadPurchaseOrders();
        loadNextNumber();
      } catch (error) {
        console.error('Error creating PO:', error);
        alert(error.message || 'Failed to create purchase order');
      }
    };

    const openReceiveForm = async (po) => {
      try {
        const response = await api.get(`/purchase-orders/${po.id}`);
        setSelectedPO(response);
        setShowReceiveForm(true);
      } catch (error) {
        console.error('Error loading PO details:', error);
        alert('Failed to load PO details');
      }
    };

    const handleReceive = async (e) => {
      e.preventDefault();
      try {
        const receivedItems = selectedPO.items
          .filter(item => parseFloat(item.quantity_to_receive || 0) > 0)
          .map(item => ({
            po_item_id: item.id,
            quantity_received: parseFloat(item.quantity_to_receive)
          }));

        if (receivedItems.length === 0) {
          alert('Please enter quantities to receive');
          return;
        }

        await api.put(`/purchase-orders/${selectedPO.purchase_order.id}/receive`, { received_items: receivedItems });
        setShowReceiveForm(false);
        setSelectedPO(null);
        loadPurchaseOrders();
        alert('Items received successfully!');
      } catch (error) {
        console.error('Error receiving items:', error);
        alert(error.message || 'Failed to receive items');
      }
    };

    const calculateTotal = () => {
      return poItems.reduce((sum, item) => {
        return sum + (parseFloat(item.quantity_ordered || 0) * parseFloat(item.unit_price || 0));
      }, 0);
    };

    return (
      <div className="responsive-container" style={{ padding: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <h1 style={{ color: '#f4d03f', margin: 0 }}>🛒 Purchase Orders</h1>
          <button
            onClick={() => setShowForm(true)}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            + Create PO
          </button>
        </div>

        {/* POs Table */}
        {loading ? (
          <p style={{ color: '#fff' }}>Loading...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>PO Number</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Vendor</th>
                  <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Amount</th>
                  <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'center', color: '#f4d03f', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrders.map((po) => (
                  <tr key={po.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <td style={{ padding: '16px', color: '#fff' }}>{po.po_number}</td>
                    <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)' }}>{formatDate(po.po_date)}</td>
                    <td style={{ padding: '16px', color: '#fff' }}>{po.vendor_name || 'N/A'}</td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#fff' }}>{formatCurrency(po.total_amount)}</td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{
                        background: po.delivery_status === 'Received' ? 'rgba(16, 185, 129, 0.1)' :
                                   po.delivery_status === 'Partial' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                        color: po.delivery_status === 'Received' ? '#10b981' :
                               po.delivery_status === 'Partial' ? '#f59e0b' : '#9ca3af',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {po.delivery_status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      {po.delivery_status !== 'Received' && po.delivery_status !== 'Cancelled' && (
                        <button
                          onClick={() => openReceiveForm(po)}
                          style={{
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            color: '#10b981',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Receive
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create PO Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              background: '#1e293b',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: '#f4d03f', marginBottom: '24px' }}>Create Purchase Order</h2>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>PO Number</label>
                    <input
                      type="text"
                      value={formData.po_number}
                      readOnly
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>PO Date *</label>
                    <input
                      type="date"
                      value={formData.po_date}
                      onChange={(e) => setFormData({ ...formData, po_date: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Delivery Date</label>
                    <input
                      type="date"
                      value={formData.delivery_date}
                      onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Vendor Name</label>
                    <input
                      type="text"
                      value={formData.vendor_name}
                      onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '14px' }}>Vendor Contact</label>
                    <input
                      type="text"
                      value={formData.vendor_contact}
                      onChange={(e) => setFormData({ ...formData, vendor_contact: e.target.value })}
                      style={{
                        width: '100%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        padding: '12px',
                        color: '#fff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ color: '#f4d03f', margin: 0 }}>Items</h3>
                    <button
                      type="button"
                      onClick={addItemToPO}
                      style={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        color: '#10b981',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      + Add Item
                    </button>
                  </div>
                  {poItems.map((item, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '12px', marginBottom: '12px', alignItems: 'end' }}>
                      <select
                        value={item.item_id}
                        onChange={(e) => updatePOItem(index, 'item_id', e.target.value)}
                        required
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          color: '#fff',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select Item</option>
                        {inventoryItems.map(invItem => (
                          <option key={invItem.id} value={invItem.id}>
                            {invItem.item_name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        step="0.001"
                        placeholder="Quantity"
                        value={item.quantity_ordered}
                        onChange={(e) => updatePOItem(index, 'quantity_ordered', e.target.value)}
                        required
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          color: '#fff',
                          fontSize: '14px'
                        }}
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updatePOItem(index, 'unit_price', e.target.value)}
                        required
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          color: '#fff',
                          fontSize: '14px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removePOItem(index)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          borderRadius: '6px',
                          padding: '12px',
                          color: '#ef4444',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'rgba(255, 255, 255, 0.05)', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>Total Amount:</span>
                    <span style={{ color: '#10b981', fontSize: '20px', fontWeight: '700' }}>{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Create PO
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setPOItems([]); }}
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#fff',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '14px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Receive Items Form Modal */}
        {showReceiveForm && selectedPO && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            overflow: 'auto'
          }}>
            <div style={{
              background: '#1e293b',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '800px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ color: '#f4d03f', marginBottom: '24px' }}>Receive Items - {selectedPO.purchase_order.po_number}</h2>
              <form onSubmit={handleReceive}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Item</th>
                      <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Ordered</th>
                      <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Received</th>
                      <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Receive Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <td style={{ padding: '12px', color: '#fff' }}>{item.item_name}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{item.quantity_ordered} {item.unit}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#10b981' }}>{item.quantity_received} {item.unit}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <input
                            type="number"
                            step="0.001"
                            max={item.quantity_ordered - item.quantity_received}
                            value={item.quantity_to_receive || ''}
                            onChange={(e) => {
                              const updated = {...selectedPO};
                              updated.items[idx].quantity_to_receive = e.target.value;
                              setSelectedPO(updated);
                            }}
                            style={{
                              width: '100px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '6px',
                              padding: '8px',
                              color: '#fff',
                              fontSize: '14px',
                              textAlign: 'right'
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Receive Items
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowReceiveForm(false); setSelectedPO(null); }}
                    style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#fff',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '14px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };
  // ============= INVENTORY REPORTS MODULE =============
  const InventoryReportsModule = () => {
    const [activeReport, setActiveReport] = useState('stock-summary');
    const [transactions, setTransactions] = useState([]);
    const [yadiSummary, setYadiSummary] = useState(null);
    const [poSummary, setPOSummary] = useState(null);
    const [stockSummary, setStockSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
      from_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0],
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear()
    });

    useEffect(() => {
      if (activeReport === 'stock-summary') {
        loadStockSummary();
      } else if (activeReport === 'transactions') {
        loadTransactions();
      } else if (activeReport === 'yadi') {
        loadYadiSummary();
      } else if (activeReport === 'po') {
        loadPOSummary();
      }
    }, [activeReport, filters]);

    const loadStockSummary = async () => {
      try {
        setLoading(true);
        const response = await api.get('/inventory-reports/stock-summary');
        setStockSummary(response);
      } catch (error) {
        console.error('Error loading stock summary:', error);
        alert('Failed to load stock summary');
      } finally {
        setLoading(false);
      }
    };

    const loadTransactions = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          from_date: filters.from_date,
          to_date: filters.to_date,
          limit: 100
        });
        const response = await api.get(`/inventory-reports/transactions?${params.toString()}`);
        setTransactions(response.data);
      } catch (error) {
        console.error('Error loading transactions:', error);
        alert('Failed to load transactions');
      } finally {
        setLoading(false);
      }
    };

    const loadYadiSummary = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          month: filters.month,
          year: filters.year
        });
        const response = await api.get(`/inventory-reports/yadi-summary?${params.toString()}`);
        setYadiSummary(response);
      } catch (error) {
        console.error('Error loading yadi summary:', error);
        alert('Failed to load yadi summary');
      } finally {
        setLoading(false);
      }
    };

    const loadPOSummary = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          month: filters.month,
          year: filters.year
        });
        const response = await api.get(`/inventory-reports/po-summary?${params.toString()}`);
        setPOSummary(response);
      } catch (error) {
        console.error('Error loading PO summary:', error);
        alert('Failed to load PO summary');
      } finally {
        setLoading(false);
      }
    };

    const TabButton = ({ id, label, icon }) => (
      <button
        onClick={() => setActiveReport(id)}
        style={{
          background: activeReport === id ? 'linear-gradient(135deg, #c9a227 0%, #f4d03f 100%)' : 'rgba(255, 255, 255, 0.05)',
          color: activeReport === id ? '#0a1628' : 'rgba(255, 255, 255, 0.6)',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 20px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <span>{icon}</span>
        {label}
      </button>
    );

    return (
      <div className="responsive-container" style={{ padding: '32px' }}>
        <h1 style={{ color: '#f4d03f', marginBottom: '24px' }}>📊 Inventory Reports</h1>

        {/* Report Tabs */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <TabButton id="stock-summary" label="Stock Summary" icon="📦" />
          <TabButton id="transactions" label="Transactions" icon="📝" />
          <TabButton id="yadi" label="Yadi Summary" icon="📋" />
          <TabButton id="po" label="PO Summary" icon="🛒" />
        </div>

        {/* Filters */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.02)',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px',
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          {activeReport === 'transactions' ? (
            <>
              <div>
                <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '13px' }}>From Date</label>
                <input
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '10px',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '13px' }}>To Date</label>
                <input
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '10px',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '13px' }}>Month</label>
                <select
                  value={filters.month}
                  onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value) })}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '10px',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'long' })}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px', fontSize: '13px' }}>Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '10px',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                  <option value="2027">2027</option>
                </select>
              </div>
            </>
          )}
        </div>

        {/* Report Content */}
        {loading ? (
          <p style={{ color: '#fff', textAlign: 'center', padding: '40px' }}>Loading...</p>
        ) : (
          <>
            {/* Stock Summary Report */}
            {activeReport === 'stock-summary' && stockSummary && (
              <div>
                {/* Grand Totals */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                  <div style={{ background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Total Purchased</p>
                    <p style={{ color: '#10b981', fontSize: '24px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatCurrency(stockSummary.grand_total.total_purchased_value)}
                    </p>
                  </div>
                  <div style={{ background: 'rgba(239, 68, 68, 0.1)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Total Used</p>
                    <p style={{ color: '#ef4444', fontSize: '24px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatCurrency(stockSummary.grand_total.total_used_value)}
                    </p>
                  </div>
                  <div style={{ background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Current Balance Value</p>
                    <p style={{ color: '#3b82f6', fontSize: '24px', fontWeight: '700', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatCurrency(stockSummary.grand_total.balance_value)}
                    </p>
                  </div>
                </div>

                {/* Category-wise Totals */}
                {stockSummary.category_totals && stockSummary.category_totals.length > 0 && (
                  <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '24px', borderRadius: '12px', marginBottom: '24px' }}>
                    <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>Category-wise Summary</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#f4d03f', fontSize: '13px' }}>Category</th>
                          <th style={{ padding: '12px', textAlign: 'center', color: '#f4d03f', fontSize: '13px' }}>Items</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '13px' }}>Purchased Value</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '13px' }}>Used Value</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '13px' }}>Balance Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockSummary.category_totals.map((cat, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                            <td style={{ padding: '12px', color: '#fff', fontWeight: '600' }}>{cat.category}</td>
                            <td style={{ padding: '12px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>{cat.item_count}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#10b981' }}>{formatCurrency(parseFloat(cat.total_purchased_value || 0))}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444' }}>{formatCurrency(parseFloat(cat.total_used_value || 0))}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#3b82f6' }}>{formatCurrency(parseFloat(cat.balance_value || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Item-wise Detail Table */}
                <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '24px', borderRadius: '12px' }}>
                  <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>Item-wise Stock Details</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase' }}>Item</th>
                          <th style={{ padding: '12px', textAlign: 'left', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase' }}>Category</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase' }}>Purchased</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase' }}>Used</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase' }}>Balance</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: '#f4d03f', fontSize: '12px', textTransform: 'uppercase' }}>Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockSummary.data.map((item, idx) => (
                          <tr key={item.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)', background: parseFloat(item.current_stock) === 0 && parseFloat(item.total_purchased) > 0 ? 'rgba(239, 68, 68, 0.05)' : 'transparent' }}>
                            <td style={{ padding: '12px', color: '#fff', fontSize: '13px' }}>{item.item_name}</td>
                            <td style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>{item.category}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#10b981', fontSize: '13px', fontFamily: "'JetBrains Mono', monospace" }}>
                              {parseFloat(item.total_purchased) > 0 ? parseFloat(item.total_purchased).toFixed(2) : '-'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#ef4444', fontSize: '13px', fontFamily: "'JetBrains Mono', monospace" }}>
                              {parseFloat(item.total_used) > 0 ? parseFloat(item.total_used).toFixed(2) : '-'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: parseFloat(item.current_stock) > 0 ? '#3b82f6' : 'rgba(255,255,255,0.3)', fontSize: '13px', fontWeight: '600', fontFamily: "'JetBrains Mono', monospace" }}>
                              {parseFloat(item.current_stock).toFixed(2)}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>{item.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Transactions Report */}
            {activeReport === 'transactions' && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.1)' }}>
                      <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Date</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Type</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Item</th>
                      <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Quantity</th>
                      <th style={{ padding: '16px', textAlign: 'right', color: '#f4d03f', fontWeight: '600' }}>Amount</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: '#f4d03f', fontWeight: '600' }}>Ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                        <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)' }}>{formatDate(txn.transaction_date)}</td>
                        <td style={{ padding: '16px' }}>
                          <span style={{
                            background: txn.transaction_type === 'Purchase' ? 'rgba(16, 185, 129, 0.1)' :
                                       txn.transaction_type === 'Usage' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                            color: txn.transaction_type === 'Purchase' ? '#10b981' :
                                   txn.transaction_type === 'Usage' ? '#ef4444' : '#9ca3af',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}>
                            {txn.transaction_type}
                          </span>
                        </td>
                        <td style={{ padding: '16px', color: '#fff' }}>{txn.item_name}</td>
                        <td style={{ padding: '16px', textAlign: 'right', color: txn.quantity >= 0 ? '#10b981' : '#ef4444' }}>
                          {txn.quantity > 0 ? '+' : ''}{txn.quantity} {txn.unit}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'right', color: '#fff' }}>{formatCurrency(Math.abs(txn.total_amount || 0))}</td>
                        <td style={{ padding: '16px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>{txn.transaction_no || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Yadi Summary Report */}
            {activeReport === 'yadi' && yadiSummary && (
              <div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  padding: '24px',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ color: '#f4d03f', marginBottom: '20px' }}>Monthly Summary</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Total Yadis</p>
                      <p style={{ color: '#fff', fontSize: '24px', fontWeight: '700', margin: 0 }}>{yadiSummary.summary.total_yadis || 0}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Total Thaals</p>
                      <p style={{ color: '#fff', fontSize: '24px', fontWeight: '700', margin: 0 }}>{yadiSummary.summary.total_thaals || 0}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Total Expense</p>
                      <p style={{ color: '#10b981', fontSize: '24px', fontWeight: '700', margin: 0 }}>
                        {formatCurrency(parseFloat(yadiSummary.summary.total_expense || 0))}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Avg Cost/Thaal</p>
                      <p style={{ color: '#f4d03f', fontSize: '24px', fontWeight: '700', margin: 0 }}>
                        {formatCurrency(parseFloat(yadiSummary.summary.avg_cost_per_thaal || 0))}
                      </p>
                    </div>
                  </div>
                </div>

                {yadiSummary.category_expense && yadiSummary.category_expense.length > 0 && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    padding: '24px',
                    borderRadius: '12px',
                    marginBottom: '24px'
                  }}>
                    <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>Category-wise Expense</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Category</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Items Used</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Expense</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yadiSummary.category_expense.map((cat, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                            <td style={{ padding: '12px', color: '#fff' }}>{cat.category}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{cat.items_used}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#fff' }}>{formatCurrency(parseFloat(cat.category_expense))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* PO Summary Report */}
            {activeReport === 'po' && poSummary && (
              <div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  padding: '24px',
                  borderRadius: '12px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ color: '#f4d03f', marginBottom: '20px' }}>Monthly Summary</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Total POs</p>
                      <p style={{ color: '#fff', fontSize: '24px', fontWeight: '700', margin: 0 }}>{poSummary.summary.total_pos || 0}</p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Total Value</p>
                      <p style={{ color: '#10b981', fontSize: '24px', fontWeight: '700', margin: 0 }}>
                        {formatCurrency(parseFloat(poSummary.summary.total_purchase_value || 0))}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Received Value</p>
                      <p style={{ color: '#3b82f6', fontSize: '24px', fontWeight: '700', margin: 0 }}>
                        {formatCurrency(parseFloat(poSummary.summary.received_value || 0))}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', margin: '0 0 8px 0' }}>Paid Value</p>
                      <p style={{ color: '#f4d03f', fontSize: '24px', fontWeight: '700', margin: 0 }}>
                        {formatCurrency(parseFloat(poSummary.summary.paid_value || 0))}
                      </p>
                    </div>
                  </div>
                </div>

                {poSummary.top_vendors && poSummary.top_vendors.length > 0 && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    padding: '24px',
                    borderRadius: '12px'
                  }}>
                    <h3 style={{ color: '#f4d03f', marginBottom: '16px' }}>Top Vendors</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Vendor</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>PO Count</th>
                          <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poSummary.top_vendors.map((vendor, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                            <td style={{ padding: '12px', color: '#fff' }}>{vendor.vendor_name}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: 'rgba(255, 255, 255, 0.6)' }}>{vendor.po_count}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#fff' }}>{formatCurrency(parseFloat(vendor.total_purchase_value))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ============= MAIN RENDER =============
  if (!isLoggedIn) {
    return <LoginScreen />;
  }

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      fontFamily: "'Crimson Pro', Georgia, serif"
    }}>
      {/* Hamburger Menu Button - Mobile Only */}
      {isMobile && (
        <button
          className="hamburger-menu"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle Menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#f4d03f' }}>
            {isSidebarOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </>
            )}
          </svg>
        </button>
      )}

      {/* Backdrop Overlay - Mobile Only */}
      {isMobile && (
        <div
          className={`sidebar-backdrop ${isSidebarOpen ? 'active' : ''}`}
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <Sidebar />
      <main className="main-content" style={{ flex: 1, overflow: 'auto', maxHeight: '100vh' }}>
        {activeModule === 'dashboard' && <Dashboard />}
        {activeModule === 'receipts' && <ReceiptsModule />}
        {activeModule === 'cashbook' && <CashBookModule />}
        {activeModule === 'takhmeen' && <TakhmeenModule />}
        {activeModule === 'expenses' && <ExpensesModule />}
        {activeModule === 'reports' && currentUser.role === 'admin' && <ReportsModule />}
        {activeModule === 'categories' && currentUser.role === 'admin' && <CategoriesModule />}
        {activeModule === 'users' && currentUser.role === 'admin' && <UsersModule />}

        {/* Inventory Management Modules */}
        {activeModule === 'inventory-dashboard' && (currentUser.role === 'inventory_manager' || currentUser.role === 'admin') && <InventoryDashboardModule />}
        {activeModule === 'inventory-items' && (currentUser.role === 'inventory_manager' || currentUser.role === 'admin') && <InventoryItemsModule />}
        {activeModule === 'daily-yadi' && (currentUser.role === 'inventory_manager' || currentUser.role === 'admin') && <DailyYadiModule />}
        {activeModule === 'purchase-orders' && (currentUser.role === 'inventory_manager' || currentUser.role === 'admin') && <PurchaseOrdersModule />}
        {activeModule === 'inventory-reports' && (currentUser.role === 'inventory_manager' || currentUser.role === 'admin') && <InventoryReportsModule />}
      </main>
    </div>
  );
}
